'use strict';

const libPictView = require('pict-view');
const libDescriptors = require('../EntityDescriptors.js');
const libLabels = require('../libLabels.js');
const libFocus = require('../FocusFilters.js');
// The translator from filter state + sort into a query the SERVER answers (WI #433). The list used to
// fetch 250 rows and sort and filter that array here, which is why sorting 1,075 blueprints descending
// by mark put 250 at the top: 250 was the largest thing this view had ever been handed.
const libFocusQuery = require('../FocusQuery.js');
// The mark is formed in exactly one place, and this is a consumer of it, not a second copy of the
// prefix and padding rules. Required directly rather than through the Markup provider because the
// stamping below runs over raw rows, and EntityMark is deliberately dependency-free.
const libEntityMark = require('../EntityMark.js');
// The mark's rendering, shared with the board and the tooltip. The list takes only the designator
// out of it: a row already rules its own columns, so it wants the mark, not the title block.
const libMarkup = require('./Markup-Templates.js');

// How long the search box waits after the last keystroke before asking the server (WI #433). Long
// enough that typing a word is one request rather than five, short enough not to feel like a lag.
const _SEARCH_DEBOUNCE_MS = 300;

/**
 * EntityList — one generic CRUD surface for every SDLC entity. showEntity(key)
 * points it at a descriptor (Vision/Module/Feature/WorkItem); it renders a
 * table in "list" mode and an inline create/edit form in "form" mode. Form
 * inputs write straight into AppData.EntityList.FormValues via their oninput
 * handlers, so there are no DOM reads — save() just posts FormValues.
 *
 * A descriptor carrying a Filter config (WorkItem) also gets a focus-view filter
 * bar: facets (Status/Type/Priority/Assignee/Target), a text search, a recent-
 * activity window, and a saved-view picker (shipped role presets + the user's own
 * named views). The active filter lives in the URL (see FocusFilters), so a
 * narrowed list is shareable and survives reload.
 */
class PictViewEntityList extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.pict.AppData.EntityList = this.pict.AppData.EntityList ||
			{
				Key: '', Title: '', HeaderCells: [], Rows: [],
				Mode: 'list', ListModeSlot: [{}], CreateButtonSlot: [], TableSlot: [], PageFootSlot: [], EmptySlot: [{}], FormSlot: [],
				// concise (the scan) or table (the full intersection); see _viewMode. TableClass
				// tightens the rows in concise, PinSlot is the concise column strip.
				ViewMode: 'concise', ViewModeSlot: [], PinSlot: [], TableClass: '',
				FormTitle: '', FormFields: [], FormValues: {}, EditingID: null, EmptyLabel: '',
				EmptyText: '', HiddenValues: {}, FormClass: '',
				// The root page-width class. List mode bleeds to fill the workspace: a wide, multi-column
				// table has no business trapped in a centered reading column -- the surplus width becomes a
				// growing left gap while the right columns clip/scroll off the edge. Form mode swaps back to
				// the centered .ps-page column (a create/edit form is reading-width). See _showList/_enterForm.
				PageClass: 'ps-page-bleed',
				// IDMedia of images uploaded from the create form before the record had an
				// id (owned by 0); save() re-owns them to the new id, abandon deletes them.
				PendingMedia: [],
				ErrorSlot: [],
				// Focus-view filtering: the live filter state, the built filter-bar model, the
				// single-element slot that renders it, and the "showing N of M" hint.
				Filter: libFocus.emptyState(), FilterBar: {}, FilterBarSlot: [], FilterCount: '',
				// The "Columns" show/hide picker (single-element slot; empty when there is no table).
				ColumnsSlot: [],
				// Paging (WI #433). Page is 1-based and is the page the store's rows came from, never
				// something derived from how many rows are loaded. ServerPaged says whether those rows
				// are one page of the server's answer (so the footer offers pages and _rows must not
				// re-sort them) or the whole set fetched for local filtering; Unexpressed says why,
				// naming each facet the query language could not carry.
				Page: 1, ServerPaged: false, Unexpressed: [],
				// The empty state's "clear the filters" affordance: an empty FILTERED list is a
				// different thing from an empty entity, and only one of them has a way out.
				EmptyFilteredSlot: []
			};
		// The descriptor field (if any) flagged Markdown:true gets the rich
		// pict-section-markdowneditor mounted in place of a plain textarea.
		this._markdownField = '';
		// The single field declared T:'code' on this descriptor, if any (WI #456).
		this._codeField = '';
		// IDUser -> roster row ({ DisplayName, IsAgent, ... }), for the assignee facet + column.
		this._rosterIndex = {};
		// TargetType -> { <id>: entity row } from the Vision/Module/Feature caches (the
		// Target column), and IDWorkItem -> linked-session count (the Sessions column).
		// Both prefetched by showEntity only when the descriptor carries such a column.
		this._targetIndex = { Vision: {}, Module: {}, Feature: {} };
		this._sessionCountIndex = {};
		// A small, deterministic palette for human avatars (the AI-agent violet is reserved,
		// so it is not in here); a name hashes to one of these.
		this._avatarPalette = ['#2988df', '#3fae6b', '#d99366', '#4aa3a3', '#c06fbb', '#5a7fce', '#c9922f', '#c0555b'];
		// The keystone customers, for Feedback's customer facet options.
		this._customerList = [];
		// Entity key -> the focus-bar config derived from a descriptor that declares none. Cached
		// because _filterConfig is called several times a render and the answer cannot change.
		this._derivedFilters = {};
		// The search box debounce timer, and the one-at-a-time list fetch (see _fetchPage). Both exist
		// because a search is now a request rather than a pass over an array already in memory.
		this._searchTimer = null;
		this._fetchInFlight = null;
		this._fetchQueued = null;
	}

	// Whether the active descriptor has at least one column carrying a given marker
	// (Assignee / Target / Sessions), so showEntity only prefetches what a list needs.
	_hasColumn(pMarker)
	{
		let tmpDescriptor = this._descriptor();
		return !!(tmpDescriptor && (tmpDescriptor.Columns || []).some((pColumn) => pColumn[pMarker]));
	}

	_descriptor() { return libDescriptors.Entities[this.pict.AppData.EntityList.Key] || null; }
	_provider()
	{
		let tmpKey = this.pict.AppData.EntityList.Key;
		return this.pict.providers[tmpKey + 'API'] || null;
	}

	// ── list preferences: how this person looks at this list ──────────────────
	// One record per user per entity, holding everything about the way a list is being read:
	//   { viewMode, columns: { table: { <F>: bool }, concise: { <F>: bool } }, sort, state }
	// where state is the full focus state (filters + search + sort). Filters used to live only in
	// the URL, which meant navigating away and back silently threw them away along with the sort;
	// the whole point of keeping them here is that a list stays how you left it.
	_listPrefs()
	{
		let tmpDescriptor = this._descriptor();
		return libFocus.loadListPrefs(tmpDescriptor ? tmpDescriptor.Key : '', this._currentUserID());
	}

	// Which of the two readings of this list is on screen.
	//   concise  the scan: the mark, the title, and the descriptor's Key columns. Tight rows,
	//            glyph columns stay glyphs, and nothing is wide enough to scroll sideways.
	//   table    the full intersection: every column not marked DefaultHidden, glyphs with their
	//            words, room to compare.
	// Reads a legacy 'density' pref so an existing user lands where they left off: the old
	// expanded/compact toggle only decided whether a glyph column printed its word, which is
	// now part of the mode rather than an axis of its own.
	_viewMode()
	{
		let tmpPrefs = this._listPrefs();
		if (tmpPrefs && tmpPrefs.viewMode) { return (tmpPrefs.viewMode === 'table') ? 'table' : 'concise'; }
		return (tmpPrefs && tmpPrefs.density === 'expanded') ? 'table' : 'concise';
	}

	// The per-mode column visibility map out of the prefs. Prefs written before the modes existed
	// carried one flat { <F>: bool } map, which was the table-mode set, so it is read as that
	// rather than being dropped on the floor.
	_columnPrefs(pPrefs, pMode)
	{
		let tmpColumns = (pPrefs && pPrefs.columns) || {};
		if (tmpColumns.table || tmpColumns.concise) { return tmpColumns[pMode] || {}; }
		return (pMode === 'table') ? tmpColumns : {};
	}

	// The focus-bar config for a list. Three lists (WorkItem, Vision, Feedback) declare one by hand
	// because their facets are particular; the other fifteen used to get nothing at all, not even a
	// search box, which is how a 20-row Features list ended up with no way to narrow it. So a
	// descriptor that declares no Filter gets one derived from its own Fields instead: text search
	// always, plus whichever of Status / Type / Priority it actually has as a select.
	//
	// Those three facets are exactly the ones FocusFilters.filterRows already knows how to apply, so
	// this widens the reach of the bar without inventing a new filter primitive. Filter: false opts
	// a list out entirely.
	_filterConfig(pDescriptor)
	{
		if (!pDescriptor || !pDescriptor.Columns || !pDescriptor.Columns.length) { return null; }
		if (pDescriptor.Filter === false) { return null; }
		if (pDescriptor.Filter) { return pDescriptor.Filter; }
		if (this._derivedFilters[pDescriptor.Key]) { return this._derivedFilters[pDescriptor.Key]; }
		let tmpFacets = ['Status', 'Type', 'Priority'].filter((pName) =>
		{
			let tmpField = (pDescriptor.Fields || []).find((pF) => pF.F === pName);
			return !!(tmpField && tmpField.T === 'select' && Array.isArray(tmpField.O) && tmpField.O.length);
		});
		this._derivedFilters[pDescriptor.Key] = { Search: true, Facets: tmpFacets, Derived: true };
		return this._derivedFilters[pDescriptor.Key];
	}

	// The default filter STATE to land on when nothing more specific applies: the user's own
	// pinned default for this screen if they set one (#236), else the descriptor's shipped
	// Filter.Defaults (WorkItem lands on the activity lens; Feedback on unaddressed only).
	// The last-used state and an explicit URL both outrank this; see _landingState.
	_effectiveDefaults(pDescriptor)
	{
		let tmpFilter = this._filterConfig(pDescriptor);
		if (!tmpFilter) { return {}; }
		let tmpUser = libFocus.loadDefaultState(pDescriptor.Key, this._currentUserID());
		if (tmpUser) { return tmpUser; }
		return tmpFilter.Defaults || {};
	}

	/**
	 * The filter state a list should open on, most specific first:
	 *
	 *   1. an explicit focus query on the URL, so a shared link or a reload of a narrowed list
	 *      still lands exactly where it pointed;
	 *   2. the state this person last left this list in;
	 *   3. their pinned per-screen default, then the descriptor's shipped one.
	 *
	 * Step 2 is the fix for the complaint. Sort and filters lived only in the URL, and the router
	 * drops the query when you navigate away, so coming back to Work Items silently reverted the
	 * sort AND reapplied the activity lens: a list showing 21 rows came back showing 2, with
	 * nothing on screen saying why.
	 */
	_landingState(pDescriptor)
	{
		if (!this._filterConfig(pDescriptor)) { return libFocus.emptyState(); }
		if (libFocus.readHashQuery()) { return libFocus.stateFromHash({}); }
		let tmpSaved = this._listPrefs().state;
		if (tmpSaved && typeof tmpSaved === 'object') { return Object.assign(libFocus.emptyState(), tmpSaved); }
		return Object.assign(libFocus.emptyState(), this._effectiveDefaults(pDescriptor));
	}

	// Remember the live filter state (which carries the sort) as this list's last-used state, so it
	// survives navigating away. Best-effort, like every other list preference.
	_persistState()
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) { return; }
		this._patchListPrefs({ state: Object.assign({}, this.pict.AppData.EntityList.Filter || {}) });
	}

	// Whether a column is locked on regardless of mode or preference. The name column keeps a list
	// from ever being blanked, and the number column is the row's mark (WI-014): identity is not
	// something a person should be able to switch off, on either side of the mode toggle.
	_isColumnLocked(pColumn, pDescriptor)
	{
		if (!pDescriptor) { return false; }
		return (pColumn.F === pDescriptor.TitleField) || (!!pDescriptor.NumberField && pColumn.F === pDescriptor.NumberField);
	}

	// Whether one column shows in the given mode. Locked columns always; then an explicit user
	// pref for THIS mode; otherwise the descriptor's default for the mode, which is DefaultHidden
	// for the full table and Key for the concise scan.
	_isColumnVisible(pColumn, pPrefs, pMode)
	{
		let tmpDescriptor = this._descriptor();
		if (this._isColumnLocked(pColumn, tmpDescriptor)) { return true; }
		let tmpColumns = this._columnPrefs(pPrefs, pMode);
		if (Object.prototype.hasOwnProperty.call(tmpColumns, pColumn.F)) { return !!tmpColumns[pColumn.F]; }
		return (pMode === 'concise') ? !!pColumn.Key : !pColumn.DefaultHidden;
	}

	// The columns actually shown, in descriptor order. Header build, cell build, and the
	// header-click sort index all read this one list so they never drift out of alignment.
	_visibleColumns()
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) { return []; }
		let tmpPrefs = this._listPrefs();
		let tmpMode = this._viewMode();
		return tmpDescriptor.Columns.filter((pColumn) => this._isColumnVisible(pColumn, tmpPrefs, tmpMode));
	}

	// ── navigation in from the sidebar ───────────────────────────────────────
	showEntity(pKey, pOptions)
	{
		let tmpDescriptor = libDescriptors.Entities[pKey];
		if (!tmpDescriptor) return;
		let tmpState = this.pict.AppData.EntityList;
		tmpState.Key = pKey;
		tmpState.Title = libLabels.title(this.pict, pKey, null, tmpDescriptor.Title);
		// Seed the live filter: an explicit URL first, then the state this person last left this
		// list in, then their pinned default, then the descriptor's shipped one (see _landingState).
		tmpState.Filter = this._landingState(tmpDescriptor);
		this._applySortFromState(tmpDescriptor);
		tmpState.HeaderCells = this._buildHeaderCells();
		tmpState.EmptyLabel = libLabels.title(this.pict, pKey, null, tmpDescriptor.Title);
		// EmptyText is owned by _syncEmptyState, which runs on every list render and knows whether the
		// emptiness is the entity's or the filter's.
		tmpState.Mode = 'list';
		let tmpProvider = this._provider();
		// Land back on the page this screen was left at, PROVIDED the store still holds the answer to
		// the SAME question: the same filter + sort, fingerprinted as QueryKey (WI #447). Paging to page
		// 4, opening a record, and coming back used to always land on page one, because this always
		// asked for page one regardless of where the store already was. A genuinely different question
		// (a new filter, a new sort, the first-ever visit) still starts at page one, same as before --
		// this only recognizes RETURNING to the one it already answered. A detail view opening in front
		// of this list no longer touches this store's Page or QueryKey while it resolves its own records
		// (EntityAPI.resolve()), so this comparison reads the list screen's own last question, not
		// whatever some other view happened to look up in passing.
		let tmpLandingPage = 1;
		if (tmpProvider)
		{
			let tmpStore = tmpProvider.store();
			let tmpLandingQuery = this._serverQuery();
			// Only an EntityAPI-backed list can fingerprint its own question. Several screens (sessions,
			// among others) are driven by a provider that extends the base class instead, which has no
			// queryKey at all -- calling it threw "queryKey is not a function" and took the whole page
			// down the moment such a list was both paged and non-empty. Without a fingerprint there is
			// no way to prove the store still answers the same question, so those screens land on page
			// one, which is exactly what every list did before WI #447.
			let fQueryKey = (tmpProvider && typeof tmpProvider.queryKey === 'function')
				? tmpProvider.queryKey.bind(tmpProvider) : null;
			if (fQueryKey && tmpLandingQuery.Paged && tmpStore.List.length
				&& tmpStore.QueryKey === fQueryKey({ Filter: tmpLandingQuery.Filter }))
			{
				tmpLandingPage = Math.max(1, Number(tmpStore.Page) || 1);
			}
		}
		tmpState.Page = tmpLandingPage;
		if (this._searchTimer) { clearTimeout(this._searchTimer); this._searchTimer = null; }
		this._fetchQueued = null;
		// A pending create-with-prefill handoff (the app's showNewFeedback stash):
		// consume it once the list is loaded and open the form instead of the list.
		let tmpPending = this.pict.AppData.EntityListPrefill;
		let tmpPrefill = (tmpPending && tmpPending.Key === pKey) ? tmpPending.Values : null;
		this.pict.AppData.EntityListPrefill = null;
		// An edit handoff (the detail view's Edit button): once the data is in, open the edit form for
		// this id instead of the list. Folding it into this single load keeps the form as the final
		// render -- a separate list()+editRecord used to race this method's own list load, and for
		// entities with a facet prefetch (WorkItem's roster) the gated list won and clobbered the form.
		let tmpEditID = (pOptions && pOptions.EditID !== null && typeof pOptions.EditID !== 'undefined') ? pOptions.EditID : null;
		// The create form as a destination of its own (the /<plural>/new route), distinct from the
		// prefill handoff which also lands on it but carries values with it.
		let tmpWantsNew = !!(pOptions && pOptions.New);
		let fAfterData = () =>
		{
			// The _apply* forms on purpose: newRecord/editRecord NAVIGATE, and this method is what the
			// route handler already called, so calling them here would send the router round again.
			if (tmpPrefill) { this._applyNewRecord(tmpPrefill); }
			else if (tmpEditID !== null) { this._applyEditRecord(tmpEditID); }
			else if (tmpWantsNew) { this._applyNewRecord(null); }
			else { this._enterList(); }
		};
		let fLoadThenList = () =>
		{
			if (tmpProvider) { this._fetchPage(tmpLandingPage).then(fAfterData).catch((pErr) => this._listLoadFailed(pErr, fAfterData)); }
			else { fAfterData(); }
		};
		// Load facet + column reference data first when needed, so cells and dropdowns resolve
		// on the first paint: the team roster (assignee facet + column), the keystone customers
		// (Feedback's customer facet), the Vision/Module/Feature caches (the Target column), and
		// the per-item session counts (the Sessions column). All best-effort; the list renders regardless.
		let tmpPrep = [];
		let tmpFilterConfig = this._filterConfig(tmpDescriptor);
		if ((tmpFilterConfig && tmpFilterConfig.Assignee) || this._hasColumn('Assignee')) { tmpPrep.push(this._loadRoster()); }
		if (tmpFilterConfig && tmpFilterConfig.Customer) { tmpPrep.push(this._loadCustomers()); }
		if (this._hasColumn('Target')) { tmpPrep.push(this._loadTargets()); }
		if (this._hasColumn('Sessions')) { tmpPrep.push(this._loadSessionCounts()); }
		// The prep loads are best-effort reference data (facet options, label caches). Promise.all
		// rejects on the first failure, which used to leave fLoadThenList unrun -- a blank page when
		// one of these hiccupped. Run the list either way (second arg = the rejection handler), so a
		// facet fetch failing degrades that dropdown, it never strands the whole screen blank.
		if (tmpPrep.length) { Promise.all(tmpPrep).then(fLoadThenList, fLoadThenList); }
		else { fLoadThenList(); }
	}

	// A list load failing (a transient server hiccup) must never strand a blank content area: surface
	// it, then still run the after-data step, which renders from whatever the provider has cached (an
	// empty state at worst). The user sees the page and can retry by re-navigating, instead of the
	// silent blank screen a rejected list() used to leave behind.
	_listLoadFailed(pErr, fAfterData)
	{
		this._toast('Could not refresh this list: ' + ((pErr && pErr.message) || pErr), 'error');
		try { fAfterData(); }
		catch (pInner) { try { this._enterList(); } catch (pE) { /* last resort: leave prior content */ } }
	}

	// Fetch the keystone customers for the Feedback customer facet options.
	// Best-effort: a failure leaves it empty (the facet still offers Any / No customer).
	_loadCustomers()
	{
		let tmpProvider = this.pict.providers.KeystoneCustomerAPI;
		if (!tmpProvider || typeof tmpProvider.list !== 'function') { this._customerList = []; return Promise.resolve([]); }
		return tmpProvider.list().then((pList) =>
		{
			this._customerList = Array.isArray(pList) ? pList.slice() : [];
			return this._customerList;
		}).catch(() => { this._customerList = []; return []; });
	}

	// Set the active Sort from (in order): the entity's live focus-view sort (in the URL,
	// for filtered entities), the user's remembered list sort (localStorage, for the rest),
	// the descriptor's DefaultSort, then the identity ordinal ascending. Keeps header-click
	// sort and view/preset sort in sync.
	_applySortFromState(pDescriptor)
	{
		let tmpState = this.pict.AppData.EntityList;
		let tmpSortStr = this._filterConfig(pDescriptor)
			? (tmpState.Filter && tmpState.Filter.sort)
			: (this._listPrefs().sort);
		let tmpParsed = libFocus.parseSort(tmpSortStr) || libFocus.parseSort(pDescriptor.DefaultSort);
		tmpState.Sort = tmpParsed
			? { Field: tmpParsed.Field, Dir: tmpParsed.Dir }
			: { Field: pDescriptor.NumberField || pDescriptor.IDField, Dir: 'asc' };
	}

	// Fetch the team roster (id -> name + IsAgent) for the assignee facet + column.
	// Best-effort: a failure leaves the lookup empty (names fall back to #id).
	_loadRoster()
	{
		let tmpTeam = this.pict.providers.TeamAPI;
		if (!tmpTeam || typeof tmpTeam.roster !== 'function') { this._rosterIndex = {}; return Promise.resolve([]); }
		return tmpTeam.roster().then((pRoster) =>
		{
			this._rosterIndex = {};
			let tmpList = Array.isArray(pRoster) ? pRoster : [];
			for (let i = 0; i < tmpList.length; i++) { this._rosterIndex[Number(tmpList[i].IDUser)] = tmpList[i]; }
			return tmpList;
		}).catch(() => { this._rosterIndex = {}; return []; });
	}

	// Fetch the Vision/Module/Feature caches and index them by id per type, so the Target
	// column can resolve a work item's TargetType + typed FK to the target's name. Loaded
	// through the same generic providers the sidebar uses. Best-effort: a missing target
	// just renders as its id. Runs the three loads together.
	_loadTargets()
	{
		let tmpTypes = ['Vision', 'Module', 'Feature'];
		let tmpLoads = tmpTypes.map((pType) =>
		{
			let tmpProvider = this.pict.providers[pType + 'API'];
			if (!tmpProvider || typeof tmpProvider.list !== 'function') { return Promise.resolve([]); }
			return tmpProvider.list()
				.then(() =>
				{
					let tmpIndex = {};
					let tmpRows = tmpProvider.store().List || [];
					let tmpIDField = 'ID' + pType;
					for (let i = 0; i < tmpRows.length; i++) { tmpIndex[Number(tmpRows[i][tmpIDField])] = tmpRows[i]; }
					this._targetIndex[pType] = tmpIndex;
					return tmpRows;
				})
				.catch(() => { this._targetIndex[pType] = {}; return []; });
		});
		return Promise.all(tmpLoads);
	}

	// Fetch the linked-session count for every work item in one call (union of the scalar
	// link and the SessionFileWorkItem join, server-side), for the Sessions column.
	// Best-effort: a failure leaves the map empty (no row shows a marker).
	_loadSessionCounts()
	{
		let tmpSessions = this.pict.providers.SessionAPI;
		if (!tmpSessions || typeof tmpSessions.sessionCounts !== 'function') { this._sessionCountIndex = {}; return Promise.resolve({}); }
		return tmpSessions.sessionCounts().then((pCounts) =>
		{
			this._sessionCountIndex = (pCounts && typeof pCounts === 'object') ? pCounts : {};
			return this._sessionCountIndex;
		}).catch(() => { this._sessionCountIndex = {}; return {}; });
	}

	_currentUserID()
	{
		let tmpAuth = this.pict.AppData.Auth;
		return (tmpAuth && tmpAuth.User && Number(tmpAuth.User.IDUser)) || 0;
	}

	_filterContext()
	{
		let tmpRoster = (this.pict.AppData.Team && this.pict.AppData.Team.Roster) || [];
		return libFocus.buildContext(tmpRoster, this._currentUserID());
	}

	// ── asking the server the question the screen is asking (WI #433) ────────

	// Whether a narrowing facet is on. Distinct from "has a filter bar": a list can offer filters and
	// have none set, and the difference is what an empty result MEANS.
	_isFiltered()
	{
		let tmpDescriptor = this._descriptor();
		if (!this._filterConfig(tmpDescriptor)) { return false; }
		return libFocus.isFiltered(this.pict.AppData.EntityList.Filter || {});
	}

	// The live filter state as FocusQuery wants it. The recent window is resolved to milliseconds
	// here because the state stores the window's NAME ('7d'), and FocusQuery is deliberately given the
	// length rather than a table of names it would have to keep in step with FocusFilters.
	_queryState(pDescriptor)
	{
		if (!this._filterConfig(pDescriptor)) { return {}; }
		let tmpFilter = this.pict.AppData.EntityList.Filter || {};
		return Object.assign({}, tmpFilter, { RecentMs: libFocus.recentMs(tmpFilter.recent) });
	}

	/**
	 * The server query for what is on screen right now, and whether it may be paged.
	 *
	 * NEVER SILENTLY PARTIAL. A non-empty Unexpressed means the query language cannot carry some part
	 * of what the screen is asking (the activity lens, "not addressed" over a nullable column, a
	 * roster-resolved assignee, a sort on a column composed in the browser). Paging then would mean
	 * applying part of the question to one page and calling the result the answer, which is precisely
	 * the defect being fixed here. So the fallback is to fetch the whole set and filter it locally --
	 * slower, and correct.
	 */
	_serverQuery()
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) { return { Filter: '', Unexpressed: [], Paged: false }; }
		let tmpBuilt = libFocusQuery.build(
			this._queryState(tmpDescriptor),
			this._filterContext(),
			tmpDescriptor,
			this.pict.AppData.EntityList.Sort);
		return { Filter: tmpBuilt.Filter, Unexpressed: tmpBuilt.Unexpressed, Paged: (tmpBuilt.Unexpressed.length === 0) };
	}

	/**
	 * Fetch one page (or, in the fallback, everything) and leave the store holding exactly that.
	 *
	 * ONE FETCH AT A TIME, AND THE LAST QUESTION ASKED WINS. Two overlapping list() calls write the
	 * same store and nothing orders their responses, so a slow answer for "auth" can land after a fast
	 * one for "authe" and leave the screen showing the answer to a question the search box has stopped
	 * asking. Serialising is cheaper than reconciling, and a queued page always re-reads the live
	 * state when it finally runs, so only the newest question is ever issued.
	 */
	_fetchPage(pPage)
	{
		let tmpProvider = this._provider();
		if (!tmpProvider || typeof tmpProvider.list !== 'function') { return Promise.resolve([]); }
		if (this._fetchInFlight)
		{
			this._fetchQueued = pPage;
			return this._fetchInFlight;
		}
		let tmpPromise = this._fetchNow(pPage).then((pRows) =>
		{
			this._fetchInFlight = null;
			if (this._fetchQueued === null) { return pRows; }
			let tmpNext = this._fetchQueued;
			this._fetchQueued = null;
			return this._fetchPage(tmpNext);
		});
		this._fetchInFlight = tmpPromise;
		return tmpPromise;
	}

	// The fetch itself. Stamps what the store now holds onto the view state, so _rows and the footer
	// read the same answer rather than each re-deriving it from the live filter.
	_fetchNow(pPage)
	{
		let tmpProvider = this._provider();
		let tmpState = this.pict.AppData.EntityList;
		let tmpQuery = this._serverQuery();
		tmpState.ServerPaged = tmpQuery.Paged;
		tmpState.Unexpressed = tmpQuery.Unexpressed;
		if (!tmpQuery.Paged)
		{
			tmpState.Page = 1;
			return tmpProvider.list({ All: true });
		}
		tmpState.Page = Math.max(1, Number(pPage) || 1);
		return tmpProvider.list({ Page: tmpState.Page, Filter: tmpQuery.Filter })
			.then((pRows) =>
			{
				// A page past the end of a set that has since shrunk (a delete, or a filter narrowed
				// while a stale page number rode along) comes back empty with rows still behind it.
				// Land on the last real page instead of showing a blank table over a full list.
				let tmpStore = tmpProvider.store();
				let tmpLast = Math.max(1, Number(tmpStore.PageCount) || 1);
				if (pRows.length || (tmpState.Page <= tmpLast)) { return pRows; }
				tmpState.Page = tmpLast;
				return tmpProvider.list({ Page: tmpLast, Filter: tmpQuery.Filter });
			});
	}

	// The list is asking a NEW question (a sort, a facet, a preset): back to page one, fetch it, then
	// draw. Every path that changes what the server would answer goes through here.
	_reload()
	{
		return this._fetchPage(1).then(() => this._enterList()).catch(() => this._enterList());
	}

	// Jump to a page from the footer controls. Clamped to what exists, and a no-op for the page
	// already on screen so a stray double-click costs nothing.
	goToPage(pPage)
	{
		let tmpProvider = this._provider();
		let tmpStore = (tmpProvider && typeof tmpProvider.store === 'function') ? tmpProvider.store() : {};
		let tmpLast = Math.max(1, Number(tmpStore.PageCount) || 1);
		let tmpWanted = Math.min(tmpLast, Math.max(1, Number(pPage) || 1));
		if (tmpWanted === Math.max(1, Number(this.pict.AppData.EntityList.Page) || 1)) { return; }
		this._fetchPage(tmpWanted).then(() => this._enterList()).catch(() => {});
	}

	_rows()
	{
		let tmpDescriptor = this._descriptor();
		let tmpProvider = this._provider();
		if (!tmpDescriptor || !tmpProvider) return [];
		// Resolve the mode once per build so every cell in this render agrees. The icon cells read
		// the derived Density off AppData: the concise scan spends an icon's width, the full table
		// draws the word beside it.
		let tmpMode = this._viewMode();
		this.pict.AppData.EntityList.ViewMode = tmpMode;
		this.pict.AppData.EntityList.Density = (tmpMode === 'table') ? 'expanded' : 'compact';
		let tmpRaw = tmpProvider.store().List || [];
		// Stamp index-backed derived columns onto the rows before sort + cell build, so both
		// read them the same way as any real field (the Sessions column sorts on _SessionCount).
		this._stampDerived(tmpRaw, tmpDescriptor);
		let tmpList = tmpRaw;
		// SORTING AND FILTERING HERE IS THE FALLBACK PATH NOW (WI #433). When the store holds one page
		// of the server's answer, that page is already ordered and narrowed by the whole set, and
		// re-sorting it here would only reorder the page -- which is exactly the bug: 250 at the top
		// and 1 at the bottom of 1,075 blueprints sorted descending. These two lines run only when
		// something in the question could not be expressed and the store therefore holds everything.
		if (!this.pict.AppData.EntityList.ServerPaged)
		{
			tmpList = this._applySort(tmpRaw, tmpDescriptor);
			if (this._filterConfig(tmpDescriptor))
			{
				tmpList = libFocus.filterRows(tmpList, this.pict.AppData.EntityList.Filter, this._filterContext());
			}
		}
		this.pict.AppData.EntityList.FilterCount = this._countLabel(tmpList.length);
		let tmpVisible = this._visibleColumns();
		return tmpList.map((pRow) =>
			({
				ID: pRow[tmpDescriptor.IDField],
				Cells: tmpVisible.map((pC) => this._cell(pC, pRow, tmpDescriptor)),
				// Addressed rows read as done: dimmed content, a "Reopen" quick action.
				RowClass: this._isAddressed(pRow, tmpDescriptor) ? 'ps-row-addressed' : '',
				AddressSlot: this._addressActionModel(pRow, tmpDescriptor)
			}));
	}

	/**
	 * Write derived fields onto the rows so the generic sort, filter and cell build can treat them
	 * as ordinary fields. Runs before all three, which is the whole reason it lives here.
	 *
	 * The mark is the important one. FocusFilters has been able to search by designator since the
	 * markups work, but it asks EntityMark.matchesQuery, which reads Designator / MarkNumber off the
	 * row. The board stamps those in Pict-Provider-BoardAPI._resolveMarks; list rows never got the
	 * equivalent, so they carried only WorkItemNumber and the mark branch quietly never fired.
	 * Typing WI-014 into the Work Items search matched nothing at all, and so did plain 14, the
	 * number printed in the first column of that very list.
	 *
	 * Both fields are display-only: they are not in any descriptor's Fields and save() posts only
	 * FormValues, so neither can post back. A provider reload replaces the rows and the next
	 * _rows() re-stamps.
	 */
	_stampDerived(pRows, pDescriptor)
	{
		let tmpIDField = pDescriptor.IDField;
		let tmpNumberField = pDescriptor.NumberField;
		if (tmpNumberField)
		{
			for (let i = 0; i < pRows.length; i++)
			{
				pRows[i].Designator = libEntityMark.designator(pDescriptor.Key, pRows[i]);
				pRows[i].MarkNumber = Number(pRows[i][tmpNumberField]) || 0;
			}
		}
		if (!this._hasColumn('Sessions')) { return; }
		let tmpCounts = this._sessionCountIndex || {};
		for (let i = 0; i < pRows.length; i++)
		{
			pRows[i]._SessionCount = Number(tmpCounts[pRows[i][tmpIDField]]) || 0;
		}
	}

	// Whether a row is addressed, per the descriptor's Addressable marker (false when
	// the entity is not addressable).
	_isAddressed(pRow, pDescriptor)
	{
		let tmpAddr = pDescriptor && pDescriptor.Addressable;
		return !!(tmpAddr && libFocus.truthy(pRow[tmpAddr.Field]));
	}

	// The Address / Reopen quick-action button model for one row. Marking straight from
	// the list -- without opening the record -- is the core of #201: a long feedback
	// backlog gets cleared in place. Empty for non-addressable entities.
	_addressActionModel(pRow, pDescriptor)
	{
		let tmpAddr = pDescriptor && pDescriptor.Addressable;
		if (!tmpAddr) { return []; }
		let tmpOn = libFocus.truthy(pRow[tmpAddr.Field]);
		return [{
			ID: pRow[pDescriptor.IDField],
			Label: tmpOn ? (tmpAddr.ReopenLabel || 'Reopen') : (tmpAddr.AddressLabel || 'Mark addressed'),
			OnClass: tmpOn ? ' ps-btn-reopen' : ' ps-btn-address'
		}];
	}

	// Flip a record's Addressed flag from the list and stamp (or clear) its date, then
	// reload so the default unaddressed filter drops it from view immediately.
	toggleAddressed(pID)
	{
		let tmpDescriptor = this._descriptor();
		let tmpProvider = this._provider();
		let tmpAddr = tmpDescriptor && tmpDescriptor.Addressable;
		if (!tmpDescriptor || !tmpProvider || !tmpAddr) { return; }
		let tmpRow = tmpProvider.store().Index[pID];
		if (!tmpRow) { return; }
		let tmpNext = libFocus.truthy(tmpRow[tmpAddr.Field]) ? 0 : 1;
		let tmpPatch = {};
		tmpPatch[tmpAddr.Field] = tmpNext;
		if (tmpAddr.DateField) { tmpPatch[tmpAddr.DateField] = tmpNext ? (new Date()).toISOString() : ''; }
		tmpProvider.update(pID, tmpPatch)
			// The page the person is on, not page one of an unfiltered list: addressing a row from
			// page three must not throw them back to the top of the list they were working through.
			.then(() => this._fetchPage(this.pict.AppData.EntityList.Page))
			.then(() =>
			{
				this._toast(tmpNext ? 'Marked addressed.' : 'Reopened.', 'success');
				this._enterList();
			})
			.catch((pErr) => this._toast('Update failed: ' + (pErr.message || pErr), 'error'));
	}

	// A cell shell with every slot empty. Each column kind fills the one slot it uses, so
	// the row template can reference them all without a missing-key gap.
	_baseCell(pColumn, pDescriptor)
	{
		return {
			// The title/name column is the long one: let it wrap and take the spare width.
			CellClass: (pColumn.F === pDescriptor.TitleField) ? 'ps-cell-wrap' : '',
			// Filled for the title column only, so a name the concise scan had to ellipsis is still
			// readable on hover instead of being simply gone.
			TitleAttr: '',
			PlainSlot: [], BadgeSlot: [], AssigneeSlot: [], TargetSlot: [], SessionSlot: [], IconSlot: [], MarkSlot: []
		};
	}

	// Escape for an HTML attribute. The title column carries user-entered text into a title="",
	// where an unescaped quote would end the attribute early and the rest would parse as markup.
	_attr(pText)
	{
		return String(pText == null ? '' : pText)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	/**
	 * The mark cell: the entity's number rendered as its designator, WI-014 rather than a bare 14.
	 *
	 * This is the column a list already had; what it printed just could not be spoken. The board,
	 * the tooltip, the Connections grid and the printed blueprint all say WI-014, so a row saying 14
	 * was the one surface where you could not quote what you were looking at.
	 *
	 * Rendered through the shared Markup-Designator template so it cannot drift from the card. The
	 * four entities with no Designator (Customer, Issue Type, Contact, Session) keep the plain
	 * number, which is what they show today.
	 */
	_markCell(pColumn, pRow, pDescriptor)
	{
		let tmpCell = this._baseCell(pColumn, pDescriptor);
		tmpCell.CellClass = 'ps-cell-mark';
		let tmpDesignator = String(pRow.Designator || '');
		if (!tmpDesignator)
		{
			let tmpRaw = pRow[pColumn.F];
			tmpCell.PlainSlot = [{ Value: (tmpRaw === null || typeof tmpRaw === 'undefined') ? '' : String(tmpRaw) }];
			return tmpCell;
		}
		tmpCell.MarkSlot = [{ Designator: tmpDesignator, KindClass: 'ps-mark-' + String(pDescriptor.Key).toLowerCase() }];
		return tmpCell;
	}

	// One rendered cell for a column. The rich, index-backed kinds (Assignee / Target /
	// Sessions) build their own avatar / chip / marker; Fmt columns run their formatter;
	// the rest print the raw value, as a (optionally tinted) pill when Badge is set.
	_cell(pColumn, pRow, pDescriptor)
	{
		// The number column is the row's mark. Detected off the descriptor rather than a flag on the
		// column, so every numbered entity gets it without eighteen descriptor edits.
		if (pDescriptor.NumberField && pColumn.F === pDescriptor.NumberField) { return this._markCell(pColumn, pRow, pDescriptor); }
		if (pColumn.Assignee) { return this._assigneeCell(pColumn, pRow, pDescriptor); }
		if (pColumn.Target) { return this._targetCell(pColumn, pRow, pDescriptor); }
		if (pColumn.Sessions) { return this._sessionCell(pColumn, pRow, pDescriptor); }
		if (pColumn.Icon) { return this._iconCell(pColumn, pRow, pDescriptor); }

		let tmpCell = this._baseCell(pColumn, pDescriptor);
		let tmpValue = pColumn.Fmt
			? String(pColumn.Fmt(pRow) || '')
			: ((pRow[pColumn.F] === null || typeof pRow[pColumn.F] === 'undefined') ? '' : String(pRow[pColumn.F]));
		if (pColumn.Badge)
		{
			let tmpTint = (pColumn.Tint && tmpValue) ? (pColumn.Tint(pRow) || '') : '';
			tmpCell.BadgeSlot = tmpValue ? [{ Value: tmpValue, StyleAttr: tmpTint ? ('style="' + this._tintStyle(tmpTint) + '"') : '' }] : [];
		}
		else
		{
			tmpCell.PlainSlot = [{ Value: tmpValue }];
		}
		if (pColumn.F === pDescriptor.TitleField && tmpValue) { tmpCell.TitleAttr = 'title="' + this._attr(tmpValue) + '"'; }
		return tmpCell;
	}

	// The Assignee cell: an avatar + name for whoever owns the work item. A human gets a
	// round, name-colored initials avatar; an AI agent (roster IsAgent) gets the hexagon
	// treatment -- a violet six-sided badge with a robot glyph -- so a mixed human/AI board
	// is legible at a glance (Vision #36). Unassigned reads as a muted placeholder, not a gap.
	_assigneeCell(pColumn, pRow, pDescriptor)
	{
		let tmpCell = this._baseCell(pColumn, pDescriptor);
		let tmpID = Number(pRow[pColumn.F]) || 0;
		let tmpPerson = tmpID ? this._rosterIndex[tmpID] : null;
		if (!tmpID)
		{
			tmpCell.AssigneeSlot = [{ Name: 'Unassigned', Initials: '', IsAgent: false, RowClass: ' ps-assignee-none', AvatarClass: '', NameClass: '', AvatarStyle: '', AssigneeAgentSlot: [] }];
			return tmpCell;
		}
		let tmpName = tmpPerson ? (tmpPerson.DisplayName || ('#' + tmpID)) : ('#' + tmpID);
		let tmpAgent = !!(tmpPerson && Number(tmpPerson.IsAgent) === 1);
		tmpCell.AssigneeSlot = [{
			Name: tmpName,
			Initials: tmpAgent ? '' : this._initials(tmpName),
			IsAgent: tmpAgent,
			RowClass: '',
			AvatarClass: tmpAgent ? ' ps-avatar-agent' : '',
			NameClass: tmpAgent ? ' ps-assignee-agent' : '',
			// A human avatar is tinted by a hash of the name; the agent hexagon owns its color.
			AvatarStyle: tmpAgent ? '' : ('style="background:' + this._avatarColor(tmpName) + '"'),
			// The AI agent's avatar carries a robot glyph in place of initials.
			AssigneeAgentSlot: tmpAgent ? [{}] : []
		}];
		return tmpCell;
	}

	// The Target cell: what the work item is about, as a type chip + the target's name
	// (resolved from the prefetched Vision/Module/Feature caches). Blank when there is no target.
	_targetCell(pColumn, pRow, pDescriptor)
	{
		let tmpCell = this._baseCell(pColumn, pDescriptor);
		let tmpType = String(pRow.TargetType || '');
		let tmpFKMap = { Vision: 'IDTargetVision', Module: 'IDTargetModule', Feature: 'IDTargetFeature' };
		if (!tmpFKMap[tmpType]) { return tmpCell; }
		let tmpID = Number(pRow[tmpFKMap[tmpType]]) || 0;
		if (!tmpID) { return tmpCell; }
		let tmpEntity = (this._targetIndex[tmpType] || {})[tmpID] || null;
		let tmpName = tmpEntity ? (tmpEntity.Name || tmpEntity.Title || ('#' + tmpID)) : ('#' + tmpID);
		tmpCell.TargetSlot = [{ Type: tmpType, TypeClass: ' ps-target-' + tmpType.toLowerCase(), Label: tmpName }];
		return tmpCell;
	}

	// The Sessions cell: a compact marker (robot glyph + count) when one or more Claude
	// sessions have advanced this work item, blank otherwise -- so the eye finds the items
	// with an auditable trail. The count is the stamped _SessionCount.
	_sessionCell(pColumn, pRow, pDescriptor)
	{
		let tmpCell = this._baseCell(pColumn, pDescriptor);
		let tmpCount = Number(pRow._SessionCount) || 0;
		if (tmpCount > 0)
		{
			tmpCell.SessionSlot = [{ Count: tmpCount, Title: tmpCount + (tmpCount === 1 ? ' linked session' : ' linked sessions') }];
		}
		return tmpCell;
	}

	// An icon cell: the column's Icon(row) names a registered glyph (rendered inline via the
	// Icon provider), with IconTitle(row) as the hover tooltip -- a compact stand-in for a short
	// enum like WorkItem Type (#236) or Feedback Source (#252). The cell still sorts on the column's
	// real field. In the list's expanded density the glyph is drawn with its word beside it; in
	// compact density (the default) it is the glyph alone. Falls back to plain text when no glyph
	// resolves (an unmapped value, or the provider being absent).
	_iconCell(pColumn, pRow, pDescriptor)
	{
		let tmpCell = this._baseCell(pColumn, pDescriptor);
		let tmpName = String(pColumn.Icon(pRow) || '');
		let tmpTitle = pColumn.IconTitle ? String(pColumn.IconTitle(pRow) || '') : '';
		let tmpProvider = this.pict.providers && this.pict.providers.Icon;
		if (tmpName && tmpProvider && typeof tmpProvider.get === 'function')
		{
			// Expanded density shows the word next to the glyph; compact shows the glyph alone (the
			// word still lives in the tooltip). Density is resolved once per render in _rows().
			let tmpExpanded = (this.pict.AppData.EntityList.Density === 'expanded');
			tmpCell.IconSlot = [{ Html: tmpProvider.get(tmpName), Title: tmpTitle, LabelSlot: (tmpExpanded && tmpTitle) ? [{ Text: tmpTitle }] : [] }];
		}
		else
		{
			let tmpRaw = (pRow[pColumn.F] === null || typeof pRow[pColumn.F] === 'undefined') ? '' : String(pRow[pColumn.F]);
			tmpCell.PlainSlot = [{ Value: tmpTitle || tmpRaw }];
		}
		return tmpCell;
	}

	// Up to two initials from a display name ('Jane Doe' -> 'JD', 'claude' -> 'C', an
	// email -> its first letter). Uppercased; falls back to '?' for an empty name.
	_initials(pName)
	{
		let tmpName = String(pName || '').trim();
		if (!tmpName) { return '?'; }
		let tmpParts = tmpName.split(/[\s@._-]+/).filter((pP) => pP.length);
		if (!tmpParts.length) { return tmpName.charAt(0).toUpperCase(); }
		let tmpFirst = tmpParts[0].charAt(0);
		let tmpSecond = (tmpParts.length > 1) ? tmpParts[1].charAt(0) : '';
		return (tmpFirst + tmpSecond).toUpperCase();
	}

	// A stable avatar background for a human name: hash the name to one palette entry, so
	// the same person is the same color everywhere without any stored preference.
	_avatarColor(pName)
	{
		let tmpName = String(pName || '');
		let tmpHash = 0;
		for (let i = 0; i < tmpName.length; i++) { tmpHash = ((tmpHash << 5) - tmpHash + tmpName.charCodeAt(i)) | 0; }
		return this._avatarPalette[Math.abs(tmpHash) % this._avatarPalette.length];
	}

	// The inline style that tints a badge from a single accent color: a soft wash background
	// with the accent as the text + border, so Status / Priority read as colored pills.
	_tintStyle(pColor)
	{
		return 'background:' + pColor + '22;color:' + pColor + ';border-color:' + pColor + '55';
	}

	_buildHeaderCells()
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) { return []; }
		let tmpSort = this.pict.AppData.EntityList.Sort || {};
		// Index is the position within the VISIBLE columns, so a header click maps back
		// through _visibleColumns() to the same column (see sortBy).
		return this._visibleColumns().map((pC, pIdx) =>
		({
			// The mark column heads the row and stays put while the table scrolls sideways, so its
			// header has to carry the same class as its cells or the two come apart.
			ThClass: (tmpDescriptor.NumberField && pC.F === tmpDescriptor.NumberField) ? ' ps-th-mark' : '',
			Label: (tmpDescriptor.NumberField && pC.F === tmpDescriptor.NumberField && tmpDescriptor.Designator) ? 'Mark' : pC.L,
			Index: pIdx,
			Caret: (tmpSort.Field === pC.F) ? (tmpSort.Dir === 'desc' ? ' ▼' : ' ▲') : ''
		}));
	}

	// Deterministic list order. Default: the entity's own identity ordinal (its number
	// column) ascending, so the oldest is first and the newest lands at the bottom. A header
	// click overrides the field and direction. Ties fall back to the id for stability, so the
	// order is never the undefined database heap order the list used to show.
	_applySort(pList, pDescriptor)
	{
		let tmpSort = this.pict.AppData.EntityList.Sort || {};
		let tmpField = tmpSort.Field || pDescriptor.NumberField || pDescriptor.IDField;
		let tmpDir = (tmpSort.Dir === 'desc') ? -1 : 1;
		let tmpIDField = pDescriptor.IDField;
		return (pList || []).slice().sort((pA, pB) =>
		{
			let tmpA = pA[tmpField], tmpB = pB[tmpField];
			let tmpNA = Number(tmpA), tmpNB = Number(tmpB);
			let tmpCmp;
			if (tmpA != null && tmpB != null && tmpA !== '' && tmpB !== '' && !isNaN(tmpNA) && !isNaN(tmpNB)) { tmpCmp = tmpNA - tmpNB; }
			else { tmpCmp = String(tmpA == null ? '' : tmpA).localeCompare(String(tmpB == null ? '' : tmpB)); }
			if (tmpCmp === 0) { tmpCmp = (Number(pA[tmpIDField]) || 0) - (Number(pB[tmpIDField]) || 0); }
			return tmpCmp * tmpDir;
		});
	}

	// Header click: sort by that column, toggling direction if it is already the sort key.
	sortBy(pIndex)
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) { return; }
		let tmpCol = this._visibleColumns()[pIndex];
		if (!tmpCol) { return; }
		let tmpState = this.pict.AppData.EntityList;
		if (tmpState.Sort && tmpState.Sort.Field === tmpCol.F) { tmpState.Sort.Dir = (tmpState.Sort.Dir === 'asc') ? 'desc' : 'asc'; }
		else { tmpState.Sort = { Field: tmpCol.F, Dir: 'asc' }; }
		let tmpSort = tmpState.Sort.Field + ':' + tmpState.Sort.Dir;
		// Remembered per user + entity in every case. It used to be written to the URL alone for a
		// filtered list, which is precisely why the sort on Work Items did not survive walking away
		// from the page: the router drops the query and the sort went with it.
		this._patchListPrefs({ sort: tmpSort });
		if (this._filterConfig(tmpDescriptor))
		{
			// Sort is also part of a shareable focus view: fold it into the URL state (and drop the
			// active preset label) so a sorted list survives reload and copy-paste.
			tmpState.Filter.sort = tmpSort;
			tmpState.Filter.view = '';
			this._writeUrl();
			this._persistState();
		}
		// THE SORT GOES TO THE SERVER (WI #433). This used to re-sort the loaded page in place, which
		// is why sorting blueprints descending by mark put 250 at the top: the page was ordered
		// perfectly and it was the wrong 250 rows. A new order is a new question, so it starts again
		// at page one.
		this._reload();
	}

	// Merge a shallow partial into the saved list prefs for this entity and persist.
	_patchListPrefs(pPatch)
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) { return; }
		let tmpPrefs = this._listPrefs();
		Object.assign(tmpPrefs, pPatch || {});
		libFocus.saveListPrefs(tmpDescriptor.Key, this._currentUserID(), tmpPrefs);
	}

	// Switch between the concise scan and the full table from the toolbar toggle, remember it per
	// user + entity, and re-render. A no-op when the mode is already active, so a stray click does
	// not thrash the DOM.
	setViewMode(pMode)
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) { return; }
		let tmpMode = (pMode === 'table') ? 'table' : 'concise';
		if (this._viewMode() === tmpMode) { return; }
		this._patchListPrefs({ viewMode: tmpMode });
		this._enterList();
	}

	// ── column show/hide ──────────────────────────────────────────────────────
	// Build the column picker model for the mode on screen: one entry per descriptor column, each
	// carrying whether it is on right now. The mark and name columns are locked on (checked +
	// disabled). Because the model is per mode, hiding Priority in the table does not also hide it
	// in the concise scan, and adding Target to the scan does not widen the table.
	_buildColumnMenu(pDescriptor)
	{
		let tmpPrefs = this._listPrefs();
		let tmpMode = this._viewMode();
		let tmpColumns = pDescriptor.Columns.map((pColumn) =>
		{
			let tmpLocked = this._isColumnLocked(pColumn, pDescriptor);
			let tmpOn = this._isColumnVisible(pColumn, tmpPrefs, tmpMode);
			return {
				F: pColumn.F,
				L: pColumn.L,
				On: tmpOn,
				Locked: tmpLocked,
				CheckedAttr: tmpOn ? 'checked' : '',
				DisabledAttr: tmpLocked ? 'disabled' : '',
				OnClass: tmpOn ? ' ps-pin-on' : '',
				// A chip reads as an action, so it says what clicking it will do.
				PinLabel: (tmpOn ? '' : '+ ') + pColumn.L
			};
		});
		return { Columns: tmpColumns, Pins: tmpColumns.filter((pColumn) => !pColumn.Locked) };
	}

	// Flip one column's visibility IN THE CURRENT MODE, persist the choice, and repaint the table
	// in place (leaving an open picker untouched so several columns can be toggled in a row).
	toggleColumn(pField)
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) { return; }
		let tmpColumn = tmpDescriptor.Columns.find((pColumn) => pColumn.F === pField);
		if (!tmpColumn) { return; }
		// The mark and name columns can never be hidden (see _isColumnLocked), so ignore them here too.
		if (this._isColumnLocked(tmpColumn, tmpDescriptor)) { return; }
		let tmpPrefs = this._listPrefs();
		let tmpMode = this._viewMode();
		let tmpNext = !this._isColumnVisible(tmpColumn, tmpPrefs, tmpMode);
		// Normalize a legacy flat map into the per-mode shape on first write, so an existing user's
		// hidden columns carry over into the table rather than silently reappearing.
		let tmpColumns = tmpPrefs.columns || {};
		if (!tmpColumns.table && !tmpColumns.concise) { tmpColumns = { table: tmpColumns, concise: {} }; }
		tmpColumns[tmpMode] = tmpColumns[tmpMode] || {};
		tmpColumns[tmpMode][pField] = tmpNext;
		tmpPrefs.columns = tmpColumns;
		libFocus.saveListPrefs(tmpDescriptor.Key, this._currentUserID(), tmpPrefs);
		// The concise pin strip shows which columns are on, so it has to redraw with the table.
		if (tmpMode === 'concise') { this._enterList(); }
		else { this._repaintTable(); }
	}

	// Repaint the header row + body (and the count) in place, without re-rendering the
	// list head -- so toggling a column keeps the "Columns" dropdown open. Falls back to a
	// full render when the table is not currently mounted (e.g. an empty list).
	_repaintTable()
	{
		let tmpState = this.pict.AppData.EntityList;
		tmpState.HeaderCells = this._buildHeaderCells();
		tmpState.Rows = this._rows();
		let tmpHeadRow = this.pict.ContentAssignment.getElement('#EntityList-HeadRow');
		if (!tmpHeadRow || tmpHeadRow.length === 0) { this._enterList(); return; }
		let tmpHead = '';
		for (let i = 0; i < tmpState.HeaderCells.length; i++) { tmpHead += this.pict.parseTemplateByHash('EntityList-Th', tmpState.HeaderCells[i]); }
		// Matches EntityList-Table: the actions column heads nothing, since two glyphs need no label.
		tmpHead += '<th class="ps-th-actions"></th>';
		this.pict.ContentAssignment.assignContent('#EntityList-HeadRow', tmpHead);
		let tmpBody = '';
		for (let i = 0; i < tmpState.Rows.length; i++) { tmpBody += this.pict.parseTemplateByHash('EntityList-Row', tmpState.Rows[i]); }
		this.pict.ContentAssignment.assignContent('#EntityList-Tbody', tmpBody);
		this._repaintFoot();
	}

	// ── focus-view filtering ─────────────────────────────────────────────────

	// A single-select facet changed (Type/Priority/Assignee/Target/Recent). Update state,
	// drop the active preset label, refresh the URL and re-render (a select has no text focus).
	setFilter(pField, pValue)
	{
		this.pict.AppData.EntityList.Filter[pField] = pValue || '';
		this.pict.AppData.EntityList.Filter.view = '';
		this._afterFilterChange();
	}

	// A status chip toggled (multi-select): add/remove the key from the comma list.
	toggleStatus(pKey)
	{
		let tmpFilter = this.pict.AppData.EntityList.Filter;
		let tmpSet = {};
		String(tmpFilter.status || '').split(',').forEach((pS) => { if (pS.trim()) { tmpSet[pS.trim()] = true; } });
		if (tmpSet[pKey]) { delete tmpSet[pKey]; } else { tmpSet[pKey] = true; }
		tmpFilter.status = Object.keys(tmpSet).join(',');
		tmpFilter.view = '';
		this._afterFilterChange();
	}

	// The search box changed. This path must NOT re-render the bar (the input would lose
	// focus mid-type), so it repaints only the table body, the footer and the count.
	//
	// DEBOUNCED, because a search is now a request rather than a pass over an array already in memory
	// (WI #433). A keystroke is not a question; the pause after one is. The state, the URL and the
	// saved preference update immediately either way, so nothing about the box lags the typing.
	setSearch(pValue)
	{
		this.pict.AppData.EntityList.Filter.q = pValue || '';
		this.pict.AppData.EntityList.Filter.view = '';
		this._writeUrl();
		this._persistState();
		if (this._searchTimer) { clearTimeout(this._searchTimer); }
		this._searchTimer = setTimeout(() =>
		{
			this._searchTimer = null;
			this._fetchPage(1).then(() => this._repaintRows()).catch(() => {});
		}, _SEARCH_DEBOUNCE_MS);
	}

	// Apply a preset / saved view by key ('' clears to no filter).
	applyView(pKey)
	{
		let tmpDescriptor = this._descriptor();
		if (!pKey) { this.clearFilters(); return; }
		let tmpState = libFocus.viewState(tmpDescriptor.Key, this._currentUserID(), pKey);
		this.pict.AppData.EntityList.Filter = tmpState || libFocus.emptyState();
		this._applySortFromState(tmpDescriptor);
		this._afterFilterChange();
	}

	clearFilters()
	{
		this.pict.AppData.EntityList.Filter = libFocus.emptyState();
		this._applySortFromState(this._descriptor());
		this._afterFilterChange();
	}

	// Save the current filter as a named personal view (name via the modal, since native
	// prompt is banned). Refreshes the picker and selects the new view.
	saveView()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpDescriptor = this._descriptor();
		let fSave = (pName) =>
		{
			let tmpRecord = libFocus.saveSavedView(tmpDescriptor.Key, this._currentUserID(), pName, this.pict.AppData.EntityList.Filter);
			if (tmpRecord)
			{
				this.pict.AppData.EntityList.Filter.view = tmpRecord.Key;
				this._afterFilterChange();
				this._toast('View "' + tmpRecord.Label + '" saved.', 'success');
			}
		};
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		tmpModal.show(
			{
				title: 'Save this view',
				content: '<p>Name this filter so you can switch back to it:</p><input type="text" id="ps-saveview-input" class="ps-input" style="width:100%" autofocus>',
				buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Save', Style: 'primary' } ]
			}).then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let tmpEl = (typeof document !== 'undefined') ? document.getElementById('ps-saveview-input') : null;
				let tmpName = (tmpEl && tmpEl.value ? tmpEl.value : '').trim();
				if (tmpName) { fSave(tmpName); }
			});
	}

	deleteView(pKey)
	{
		let tmpKey = pKey || this.pict.AppData.EntityList.Filter.view;
		if (!tmpKey) { return; }
		libFocus.deleteSavedView(this._descriptor().Key, this._currentUserID(), tmpKey);
		if (this.pict.AppData.EntityList.Filter.view === tmpKey) { this.pict.AppData.EntityList.Filter.view = ''; }
		this._afterFilterChange();
		this._toast('View deleted.', 'success');
	}

	// ── per-screen default filter (#236) ──────────────────────────────────────
	// Pin the current filter as this user's default landing view for this screen. "None" is pinnable
	// too: Clear the filters first, then Set as default.
	//
	// The pinned default now sits UNDER the last-used state (see _landingState), so pinning one also
	// rewrites the last-used state to match. Otherwise the control would look broken: you would pin
	// a default, and the state you happened to be in would keep outranking it forever.
	setAsDefault()
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor || !this._filterConfig(tmpDescriptor)) { return; }
		libFocus.saveDefaultState(tmpDescriptor.Key, this._currentUserID(), this.pict.AppData.EntityList.Filter);
		this._persistState();
		let tmpName = libLabels.title(this.pict, tmpDescriptor.Key, null, tmpDescriptor.Title);
		this._toast('Saved as your default view for ' + tmpName + '.', 'success');
		// Repaint the bar so the Clear-default control appears.
		this._enterList();
	}

	// Drop the user's pinned default for this screen; the shipped default takes over on the next
	// clean landing. The remembered last-used state goes with it, since it would otherwise outrank
	// the shipped default and nothing would appear to change. The current view stays on screen.
	clearDefault()
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor || !this._filterConfig(tmpDescriptor)) { return; }
		libFocus.clearDefaultState(tmpDescriptor.Key, this._currentUserID());
		this._patchListPrefs({ state: null });
		this._toast('Default view cleared; using the standard default next time.', 'success');
		this._enterList();
	}

	// Shared post-change tail for every facet mutation: remember the state, mirror it to the URL,
	// and re-render. Persisting here is what makes a narrowed list survive walking away from it,
	// and it is what makes Clear honest -- clearing used to be undone the moment you came back,
	// because the descriptor's default lens reapplied itself with nothing on screen saying so.
	_afterFilterChange()
	{
		this._writeUrl();
		this._persistState();
		// A changed facet is a different question, so it is asked again from page one rather than
		// applied to the page already in hand (WI #433).
		this._reload();
	}

	_writeUrl()
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor || !this._filterConfig(tmpDescriptor)) { return; }
		libFocus.writeHashState(this.pict.AppData.EntityList.Filter);
	}

	// Concatenate one template rendered once per record in a TS: slot array -- the same expansion
	// {~TS:Hash:Slot~} does inline, usable outside a full render.
	_renderSlot(pHash, pSlotArray)
	{
		let tmpHTML = '';
		(pSlotArray || []).forEach((pRecord) => { tmpHTML += this.pict.parseTemplateByHash(pHash, pRecord); });
		return tmpHTML;
	}

	// Recompute + repaint ONLY the table (or the empty state) and the count -- used by the search
	// box so the input keeps focus. A search can narrow the list to zero rows and back on every
	// keystroke; #EntityList-Tbody only exists while there are rows (EntityList-Table is itself a
	// TS: slot that drops out when TableSlot is empty), so probing for the tbody used to fall back
	// to a full _enterList() render the moment a search matched nothing -- which rebuilds the filter
	// bar too and defocuses the box on every character from then on. #EntityList-Body wraps the
	// table/empty-state pair with a stable id, so it repaints in place without ever touching the
	// filter bar.
	_repaintRows()
	{
		let tmpState = this.pict.AppData.EntityList;
		tmpState.Rows = this._rows();
		tmpState.TableSlot = (tmpState.Rows.length > 0) ? [{}] : [];
		tmpState.EmptySlot = (tmpState.Rows.length === 0) ? [{}] : [];
		this._syncEmptyState();
		let tmpBody = this.pict.ContentAssignment.getElement('#EntityList-Body');
		if (!tmpBody || tmpBody.length === 0)
		{
			this._enterList();
			return;
		}
		let tmpHTML = this._renderSlot('EntityList-Table', tmpState.TableSlot)
			+ this._renderSlot('EntityList-Empty', tmpState.EmptySlot);
		this.pict.ContentAssignment.assignContent('#EntityList-Body', tmpHTML);
		this._repaintFoot();
	}

	// The page footer and the count, repainted in place. BOTH partial repaint paths have to call this
	// or they leave a stale page number under fresh rows: the search box (_repaintRows) changes the
	// result set out from under "Page 2 of 5", and the column toggle (_repaintTable) redraws the table
	// without ever touching what sits below it. Its own stable container, so it repaints whether or
	// not there is a footer to draw right now.
	_repaintFoot()
	{
		let tmpState = this.pict.AppData.EntityList;
		tmpState.PageFootSlot = this._pageFootModel();
		this.pict.ContentAssignment.assignContent('#EntityList-Foot', this._renderSlot('EntityList-PageFoot', tmpState.PageFootSlot));
		this.pict.ContentAssignment.assignContent('#EntityList-Count', tmpState.FilterCount || '');
	}

	// Build the filter-bar model (facet controls + view picker) from the descriptor's
	// Filter config and the live state, then park it in the single-element render slot.
	_syncFilterSlot(pDescriptor)
	{
		let tmpState = this.pict.AppData.EntityList;
		if (this._filterConfig(pDescriptor))
		{
			this._buildFilterBar(pDescriptor);
			tmpState.FilterBarSlot = [tmpState.FilterBar];
		}
		else
		{
			tmpState.FilterBarSlot = [];
		}
	}

	_fieldOptions(pDescriptor, pF)
	{
		let tmpField = (pDescriptor.Fields || []).find((pX) => pX.F === pF);
		return (tmpField && tmpField.O) ? tmpField.O.slice() : [];
	}

	// A generic <option> model list: '' renders as pAnyLabel (the "no filter" choice), the
	// rest use their own text; the one matching pSelected is marked selected.
	_selectOptions(pValues, pSelected, pAnyLabel)
	{
		return pValues.map((pV) =>
			({ Value: pV, Label: (pV === '') ? pAnyLabel : pV, SelectedAttr: (String(pV) === String(pSelected || '')) ? 'selected' : '' }));
	}

	_assigneeOptions(pSelected)
	{
		let tmpOptions = libFocus.AssigneeChoices.map((pC) =>
			({ Value: pC.Key, Label: pC.Label, SelectedAttr: (pC.Key === (pSelected || '')) ? 'selected' : '' }));
		// Specific people from the roster (agents flagged), keyed as u:<IDUser>.
		let tmpRoster = (this.pict.AppData.Team && this.pict.AppData.Team.Roster) || [];
		for (let i = 0; i < tmpRoster.length; i++)
		{
			let tmpKey = 'u:' + tmpRoster[i].IDUser;
			tmpOptions.push(
				{
					Value: tmpKey,
					Label: (tmpRoster[i].DisplayName || ('#' + tmpRoster[i].IDUser)) + (Number(tmpRoster[i].IsAgent) === 1 ? ' (AI)' : ''),
					SelectedAttr: (tmpKey === (pSelected || '')) ? 'selected' : ''
				});
		}
		return tmpOptions;
	}

	_targetOptions(pSelected)
	{
		return libFocus.TargetChoices.map((pC) =>
			({ Value: pC.Key, Label: pC.Label, SelectedAttr: (pC.Key === (pSelected || '')) ? 'selected' : '' }));
	}

	_workflowStatusOptions(pSelected)
	{
		return libFocus.WorkflowStatusChoices.map((pC) =>
			({ Value: pC.Key, Label: pC.Label, SelectedAttr: (pC.Key === (pSelected || '')) ? 'selected' : '' }));
	}

	_recentOptions(pSelected)
	{
		let tmpOptions = [{ Value: '', Label: 'Any time', SelectedAttr: (!pSelected) ? 'selected' : '' }];
		libFocus.RecentWindows.forEach((pW) =>
			tmpOptions.push({ Value: pW.Key, Label: pW.Label, SelectedAttr: (pW.Key === (pSelected || '')) ? 'selected' : '' }));
		return tmpOptions;
	}

	// Addressed facet: 'no' (default) / 'yes' / 'all'. Both '' and 'all' read as "show
	// everything", so a Clear (which empties the state) still shows the All option selected.
	_addressedOptions(pSelected)
	{
		let tmpCur = String(pSelected || '');
		let tmpAll = (tmpCur === '' || tmpCur === 'all');
		return [
			{ Value: 'no',  Label: 'Unaddressed',  SelectedAttr: (tmpCur === 'no') ? 'selected' : '' },
			{ Value: 'yes', Label: 'Addressed',    SelectedAttr: (tmpCur === 'yes') ? 'selected' : '' },
			{ Value: 'all', Label: 'All feedback', SelectedAttr: tmpAll ? 'selected' : '' }
		];
	}

	// Customer facet: Any / No customer, then each loaded keystone customer by name.
	_customerOptions(pSelected)
	{
		let tmpCur = String(pSelected || '');
		let tmpOptions =
		[
			{ Value: '',     Label: 'Any customer', SelectedAttr: (tmpCur === '') ? 'selected' : '' },
			{ Value: 'none', Label: 'No customer',  SelectedAttr: (tmpCur === 'none') ? 'selected' : '' }
		];
		let tmpList = (this._customerList || []).slice().sort((pA, pB) => String(pA.Name || '').localeCompare(String(pB.Name || '')));
		for (let i = 0; i < tmpList.length; i++)
		{
			let tmpKey = String(tmpList[i].IDKeystoneCustomer);
			tmpOptions.push({ Value: tmpKey, Label: tmpList[i].Name || ('#' + tmpKey), SelectedAttr: (tmpKey === tmpCur) ? 'selected' : '' });
		}
		return tmpOptions;
	}

	// Source-type facet: Any source, then the descriptor's SourceType option list.
	_sourceTypeOptions(pDescriptor, pSelected)
	{
		return this._selectOptions([''].concat(this._fieldOptions(pDescriptor, 'SourceType')), pSelected, 'Any source');
	}

	_viewOptions(pSelected)
	{
		let tmpOptions = [{ Value: '', Label: 'Views: none', SelectedAttr: (!pSelected) ? 'selected' : '' }];
		let tmpAll = libFocus.allViews(this._descriptor().Key, this._currentUserID());
		tmpAll.forEach((pV) =>
			tmpOptions.push({ Value: pV.Key, Label: (pV.Group ? (pV.Group + ': ') : '') + pV.Label, SelectedAttr: (pV.Key === (pSelected || '')) ? 'selected' : '' }));
		return tmpOptions;
	}

	_buildFilterBar(pDescriptor)
	{
		let tmpState = this.pict.AppData.EntityList;
		let tmpFilter = tmpState.Filter || libFocus.emptyState();
		let tmpConfig = this._filterConfig(pDescriptor) || {};
		let tmpFacets = tmpConfig.Facets || [];
		let fHas = (pName) => tmpFacets.indexOf(pName) !== -1;
		let tmpStatusSet = {};
		String(tmpFilter.status || '').split(',').forEach((pS) => { if (pS.trim()) { tmpStatusSet[pS.trim()] = true; } });

		tmpState.FilterBar =
		{
			SearchSlot: tmpConfig.Search ? [{ Value: String(tmpFilter.q || '') }] : [],
			// Status as multi-toggle chips (matches the board lanes).
			StatusChips: fHas('Status')
				? this._fieldOptions(pDescriptor, 'Status').map((pO) => ({ Key: pO, Label: (libDescriptors.BoardLaneLabels[pO] || pO), OnClass: tmpStatusSet[pO] ? ' ps-chip-on' : '' }))
				: [],
			// Single-select dropdown facets.
			TypeSlot: fHas('Type') ? [{ Options: this._selectOptions([''].concat(this._fieldOptions(pDescriptor, 'Type')), tmpFilter.type, 'All types') }] : [],
			PrioritySlot: fHas('Priority') ? [{ Options: this._selectOptions([''].concat(this._fieldOptions(pDescriptor, 'Priority')), tmpFilter.priority, 'Any priority') }] : [],
			AssigneeSlot: fHas('Assignee') ? [{ Options: this._assigneeOptions(tmpFilter.assignee) }] : [],
			TargetSlot: fHas('Target') ? [{ Options: this._targetOptions(tmpFilter.target) }] : [],
			WorkflowStatusSlot: fHas('WorkflowStatus') ? [{ Options: this._workflowStatusOptions(tmpFilter.wfstatus) }] : [],
			// Feedback facets: addressed lifecycle, source customer, and origin source-type.
			AddressedSlot: fHas('Addressed') ? [{ Options: this._addressedOptions(tmpFilter.addressed) }] : [],
			CustomerSlot: fHas('Customer') ? [{ Options: this._customerOptions(tmpFilter.customer) }] : [],
			SourceTypeSlot: fHas('SourceType') ? [{ Options: this._sourceTypeOptions(pDescriptor, tmpFilter.sourcetype) }] : [],
			RecentSlot: tmpConfig.Recent ? [{ Options: this._recentOptions(tmpFilter.recent) }] : [],
			ViewSlot: tmpConfig.Views ? [{ Options: this._viewOptions(tmpFilter.view) }] : [],
			CountLabel: tmpState.FilterCount || '',
			ClearSlot: libFocus.isFiltered(tmpFilter) ? [{}] : [],
			// Only a saved (personal) view can be deleted; presets are shipped.
			DeleteViewSlot: (String(tmpFilter.view || '').indexOf('saved:') === 0) ? [{ Key: tmpFilter.view }] : [],
			// Per-screen default (#236): "Set as default" is always offered on a filtered list;
			// "Clear default" only when the user has pinned one for this screen.
			SetDefaultSlot: [{}],
			ClearDefaultSlot: libFocus.loadDefaultState(pDescriptor.Key, this._currentUserID()) ? [{}] : []
		};
	}

	// The provider's store, or an empty object when there is no provider yet.
	_pageStore()
	{
		let tmpProvider = this._provider();
		return (tmpProvider && typeof tmpProvider.store === 'function') ? (tmpProvider.store() || {}) : {};
	}

	/**
	 * The sentence that says what is on screen.
	 *
	 * Server-paged it is a RANGE out of the real total, because "Showing 250 of 1075" over a page of
	 * 250 reads as a truncation rather than as page two -- which is how the original bug hid in plain
	 * sight. In the local fallback the store holds everything, so it is the old N-of-M count and it is
	 * true again for the first time.
	 */
	_countLabel(pShown)
	{
		let tmpState = this.pict.AppData.EntityList;
		let tmpStore = this._pageStore();
		if (!tmpState.ServerPaged)
		{
			if (!this._filterConfig(this._descriptor())) { return ''; }
			let tmpLoaded = ((tmpStore.List) || []).length;
			return 'Showing ' + pShown + ' of ' + tmpLoaded;
		}
		let tmpTotal = Number(tmpStore.Total) || 0;
		// An empty list is only a "no matches" when something was asked of it. On an entity nobody has
		// created anything in yet, "No matches" reads as a failed search beside an empty state that
		// says the opposite, so the honest answer there is to say nothing and let the empty state talk.
		if (!tmpTotal || !pShown) { return this._isFiltered() ? 'No matches' : ''; }
		let tmpFirst = ((Math.max(1, Number(tmpStore.Page) || 1)) - 1) * (Math.max(1, Number(tmpStore.PageSize) || 1)) + 1;
		return 'Showing ' + tmpFirst + '-' + (tmpFirst + pShown - 1) + ' of ' + tmpTotal;
	}

	/**
	 * The page controls: first / prev / page N of M / next / last, and the range they are moving
	 * through. Real pages, not a Load more -- a list of 1,075 rows should be walkable, and the fifth
	 * page should be one click from the first rather than four appends away from it.
	 *
	 * Empty when there is only one page (nothing to move between) and in the local fallback (which
	 * holds the whole set and has no pages at all).
	 */
	_pageFootModel()
	{
		let tmpState = this.pict.AppData.EntityList;
		if (!tmpState.ServerPaged) { return []; }
		let tmpStore = this._pageStore();
		let tmpCount = Math.max(1, Number(tmpStore.PageCount) || 1);
		if (tmpCount < 2) { return []; }
		let tmpPage = Math.min(tmpCount, Math.max(1, Number(tmpStore.Page) || 1));
		let tmpAtStart = (tmpPage <= 1);
		let tmpAtEnd = (tmpPage >= tmpCount);
		return [{
			RangeLabel: tmpState.FilterCount || '',
			PageLabel: 'Page ' + tmpPage + ' of ' + tmpCount,
			PrevPage: tmpPage - 1,
			NextPage: tmpPage + 1,
			LastPage: tmpCount,
			FirstDisabled: tmpAtStart ? 'disabled' : '',
			PrevDisabled: tmpAtStart ? 'disabled' : '',
			NextDisabled: tmpAtEnd ? 'disabled' : '',
			LastDisabled: tmpAtEnd ? 'disabled' : ''
		}];
	}

	/**
	 * What an empty table means, which is now two different things.
	 *
	 * Rows.length === 0 used to be unambiguous, because the browser held every row and had filtered
	 * them itself. With the filtering server-side an empty page is far more often "nothing matched"
	 * than "nothing exists", and telling someone their Work Items list is empty while it holds six
	 * hundred rows behind a facet is a lie the old local filtering was never able to tell. So the
	 * filtered empty says so, and carries the way out with it.
	 */
	_syncEmptyState()
	{
		let tmpState = this.pict.AppData.EntityList;
		let tmpDescriptor = this._descriptor();
		if (this._isFiltered())
		{
			tmpState.EmptyText = 'No ' + (tmpState.EmptyLabel || 'records') + ' match these filters.';
			tmpState.EmptyFilteredSlot = [{}];
			return;
		}
		tmpState.EmptyText = (tmpDescriptor && tmpDescriptor.EmptyText) || 'Nothing here yet. Click + New to add the first one.';
		tmpState.EmptyFilteredSlot = [];
	}

	_enterList()
	{
		// Any create-form images still pending here are being abandoned (save() re-owns
		// and clears them before it reaches this), so drop the orphan uploads.
		this._discardPendingMedia();
		// Leaving the form: the render below wipes the rich editor's host DOM, but the editor view
		// itself lingers (its CodeMirror instances + debounce timers stay live against detached
		// nodes). Destroy it here so it does not accumulate as a detached-node leak across create /
		// edit cycles; _mountMarkdownEditor re-creates it cleanly the next time a form opens.
		this._teardownMarkdownEditor();
		this._teardownCodeEditor();
		let tmpDescriptor = this._descriptor();
		let tmpState = this.pict.AppData.EntityList;
		tmpState.Mode = 'list';
		tmpState.Rows = this._rows();
		tmpState.HeaderCells = this._buildHeaderCells();
		// Entities flagged NoCreate (test runs start from a Routine) hide "+ New".
		tmpState.ListModeSlot = (tmpDescriptor && tmpDescriptor.NoCreate) ? [] : [{}];
		// A descriptor can supply a custom primary action in place of "+ New"
		// (Session uploads a zip rather than opening the inline create form).
		tmpState.CreateButtonSlot = (tmpDescriptor && tmpDescriptor.CreateButton)
			? [{ Label: tmpDescriptor.CreateButton.Label, Action: tmpDescriptor.CreateButton.Action }]
			: [];
		tmpState.TableSlot = (tmpState.Rows.length > 0) ? [{}] : [];
		tmpState.EmptySlot = (tmpState.Rows.length === 0) ? [{}] : [];
		this._syncEmptyState();
		// Real pages (WI #433): first / prev / page N of M / next / last over the server's answer.
		tmpState.PageFootSlot = this._pageFootModel();
		// The column controls ride alongside the table, only when there is a table to shape (a
		// descriptor with columns) and only when there is something unlocked to shape: a two-column
		// list like Blueprints has nothing to offer.
		//
		// SHOWN OVER AN EMPTY FILTERED LIST TOO. Gating these on rows alone meant a filter that matched
		// nothing took the whole toolbar away with it, including the mode toggle -- the controls a
		// person reaches for when a list has gone blank are the ones that vanish exactly then.
		let tmpMode = this._viewMode();
		let tmpHasTable = (tmpState.Rows.length > 0) || this._isFiltered();
		let tmpMenu = (tmpHasTable && tmpDescriptor && tmpDescriptor.Columns && tmpDescriptor.Columns.length)
			? this._buildColumnMenu(tmpDescriptor)
			: null;
		let tmpHasOptional = !!(tmpMenu && tmpMenu.Pins.length);
		// One control per mode, each shaped for its job. The table gets the dropdown, which scales to
		// nine columns without eating a row of the toolbar. The concise scan gets the strip, because
		// adding a column back for a minute to cross-reference something should be one click and
		// should advertise what is available rather than hiding it behind a menu.
		tmpState.ColumnsSlot = (tmpHasOptional && tmpMode === 'table') ? [tmpMenu] : [];
		tmpState.PinSlot = (tmpHasOptional && tmpMode === 'concise') ? [tmpMenu] : [];
		// The mode toggle rides alongside the table whenever there are rows to read.
		tmpState.ViewModeSlot = (tmpHasTable && tmpDescriptor && tmpDescriptor.Columns && tmpDescriptor.Columns.length)
			? [{
				ConciseOnClass: (tmpMode === 'concise') ? ' ps-density-on' : '',
				TableOnClass: (tmpMode === 'table') ? ' ps-density-on' : ''
			}]
			: [];
		// The concise scan tightens every row; the table keeps the roomier one.
		tmpState.TableClass = (tmpMode === 'concise') ? ' ps-table-concise' : '';
		tmpState.FormSlot = [];
		// Fill the workspace: the table (and the empty state) get the full width instead of a centered
		// 1000px column; .ps-table-scroll handles genuine overflow only when the window is narrower than
		// the table itself.
		tmpState.PageClass = 'ps-page-bleed';
		this._syncFilterSlot(tmpDescriptor);
		this.render();
	}

	// ── form mode ────────────────────────────────────────────────────────────
	// A field carrying HideWhen is dropped from the form when the named sibling field holds the matching
	// value (used to hide the likert scores for Developer Experience feedback).
	_fieldHidden(pField, pValues)
	{
		let tmpCond = pField && pField.HideWhen;
		if (!tmpCond || !tmpCond.Field) { return false; }
		return String((pValues || {})[tmpCond.Field]) === String(tmpCond.Equals);
	}

	_buildFormFields()
	{
		let tmpDescriptor = this._descriptor();
		let tmpValues = this.pict.AppData.EntityList.FormValues;
		return tmpDescriptor.Fields.filter((pField) => !this._fieldHidden(pField, tmpValues)).map((pField) =>
		{
			let tmpVal = (tmpValues[pField.F] === null || typeof tmpValues[pField.F] === 'undefined') ? '' : tmpValues[pField.F];
			return (
			{
				F: pField.F, L: pField.L,
				TextSlot: (pField.T === 'text') ? [{ F: pField.F, Value: tmpVal }] : [],
				TextareaSlot: (pField.T === 'textarea' && !pField.Markdown) ? [{ F: pField.F, Value: tmpVal }] : [],
				// Markdown:true -> the rich editor mounts into the host div after render (onAfterRender).
				MarkdownSlot: (pField.T === 'textarea' && pField.Markdown) ? [{ F: pField.F }] : [],
				// T:'code' -> pict-section-code mounts the same way. A configuration blob is not prose, and
				// a bare textarea makes a person balance braces with no line numbers and no syntax colour.
				CodeSlot: (pField.T === 'code') ? [{ F: pField.F }] : [],
				SelectSlot: (pField.T === 'select') ? [{
					F: pField.F,
					Options: (pField.O || []).map((pO) =>
						({ Value: pO, SelectedAttr: (String(pO) === String(tmpVal)) ? 'selected' : '' }))
				}] : [],
				// A native date picker; the stored value is an ISO string, trimmed to yyyy-mm-dd.
				DateSlot: (pField.T === 'date') ? [{ F: pField.F, Value: (tmpVal ? String(tmpVal).slice(0, 10) : '') }] : [],
				// Likert: a 0-5 segmented row; 0 ('n/a') means unanswered.
				LikertSlot: (pField.T === 'likert') ? [{
					Buttons: [0, 1, 2, 3, 4, 5].map((pN) => (
					{
						F: pField.F, N: pN,
						Label: (pN === 0) ? 'n/a' : String(pN),
						OnClass: ((Number(tmpVal) || 0) === pN) ? ' ps-likert-on' : ''
					}))
				}] : [],
				// A boolean (stored 0/1): a native checkbox. Checked when the stored value is truthy.
				CheckboxSlot: (pField.T === 'checkbox') ? [{ F: pField.F, CheckedAttr: (String(tmpVal) === '1' || tmpVal === 1 || tmpVal === true) ? 'checked' : '' }] : [],
				// A type-or-pick field: a text input backed by a <datalist> of suggestions (today the
				// capability/action values a granted node advertises). Free text still saves, so an action
				// can be named before the node that serves it is connected. _loadComboSources fills the list.
				ComboSlot: (pField.T === 'combo') ? [{ F: pField.F, Value: tmpVal, ListID: 'EntityForm-combo-' + pField.F, Options: this._comboOptions(pField, tmpValues) }] : []
			});
		});
	}

	_enterForm()
	{
		let tmpState = this.pict.AppData.EntityList;
		tmpState.Mode = 'form';
		tmpState.FormFields = this._buildFormFields();
		let tmpDescriptor = this._descriptor() || { Fields: [] };
		this._markdownField = (tmpDescriptor.Fields.find((pF) => pF.Markdown && pF.T === 'textarea') || {}).F || '';
		this._codeField = (tmpDescriptor.Fields.find((pF) => pF.T === 'code') || {}).F || '';
		tmpState.FormClass = (this._markdownField || this._codeField) ? 'ps-form-wide' : '';
		tmpState.ListModeSlot = [];
		tmpState.CreateButtonSlot = [];
		tmpState.TableSlot = [];
		tmpState.EmptySlot = [];
		tmpState.FilterBarSlot = [];
		tmpState.ColumnsSlot = [];
		tmpState.FormSlot = [{}];
		// Back to the centered reading column while the create/edit form is open.
		tmpState.PageClass = 'ps-page';
		this.render();
		// Load any combo-field suggestions (e.g. advertised node capabilities) and fill the datalists.
		this._loadComboSources();
	}

	// Open the create form. pPrefill (optional) seeds it: keys matching descriptor
	// Fields land in the form; every key also rides along in HiddenValues so
	// non-field FKs (IDTestRun, IDKeystoneCustomer) persist through save().
	/**
	 * Open the create form. NAVIGATES; the route handler renders.
	 *
	 * The form used to render in place at the list's own URL, which cost a person real work: a reload
	 * dropped a half-written record with no warning, Back left the whole list screen rather than
	 * closing the form, and "+ New" could not be linked to anyone. It cost the harness too -- because
	 * the URL never changed, asking the router for the list route again re-fired nothing, so a form
	 * left open simply stayed and every later row lookup reported healthy records missing.
	 *
	 * The render lives in _applyNewRecord, which showEntity calls once its data is in. This must NOT
	 * call that path itself and _applyNewRecord must NOT call this one, or the route loops.
	 */
	newRecord(pPrefill)
	{
		let tmpKey = this.pict.AppData.EntityList.Key;
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showEntityNew === 'function')
		{
			// The prefill rides in the stash showEntity already consumes for the create-from-here handoff.
			if (pPrefill) { this.pict.AppData.EntityListPrefill = { Key: tmpKey, Values: pPrefill }; }
			if (tmpApp.showEntityNew(tmpKey)) { return; }
			// No list slug for this entity, so there is no route to go to. Render in place, as before.
			this.pict.AppData.EntityListPrefill = null;
		}
		this._applyNewRecord(pPrefill);
	}

	/** Open the edit form for one record. NAVIGATES; the route handler renders. See newRecord. */
	editRecord(pID)
	{
		let tmpKey = this.pict.AppData.EntityList.Key;
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showEntityEdit === 'function' && tmpApp.showEntityEdit(tmpKey, pID)) { return; }
		this._applyEditRecord(pID);
	}

	_applyNewRecord(pPrefill)
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) return;
		this.pict.AppData.EntityList.EditingID = null;
		this.pict.AppData.EntityList.PendingMedia = [];
		this.pict.AppData.EntityList.FormTitle = 'New ' + libLabels.singular(this.pict, tmpDescriptor.Key, null, tmpDescriptor.Key);
		let tmpValues = {};
		tmpDescriptor.Fields.forEach((pField) =>
		{
			tmpValues[pField.F] = (pField.T === 'select' && pField.O && pField.O.length)
				? pField.O[0]
				: ((pField.T === 'likert' || pField.T === 'checkbox' || pField.E) ? 0 : '');
			if (pPrefill && typeof pPrefill[pField.F] !== 'undefined') { tmpValues[pField.F] = pPrefill[pField.F]; }
		});
		this.pict.AppData.EntityList.FormValues = tmpValues;
		this.pict.AppData.EntityList.HiddenValues = Object.assign({}, pPrefill || {});
		this._enterForm();
	}

	_applyEditRecord(pID)
	{
		let tmpDescriptor = this._descriptor();
		let tmpProvider = this._provider();
		if (!tmpDescriptor || !tmpProvider) return;
		let tmpRow = tmpProvider.store().Index[pID];
		if (!tmpRow)
		{
			// NOT IN THE LOADED PAGE, WHICH IS NOT THE SAME AS NOT EXISTING. Now that the edit form has a
			// URL, it gets opened cold, and a record past the capped list page would silently open
			// nothing at all -- the same gap that made every blueprint past the first page unreachable
			// (WI #415). Ask for it by id, cache it, and carry on. read() resolves null for a record that
			// really is gone, and then this stops.
			if (typeof tmpProvider.read !== 'function') { return; }
			return tmpProvider.read(pID).then((pRecord) =>
			{
				if (!pRecord) { return; }
				tmpProvider.cacheRecord(pRecord);
				return this._applyEditRecord(pID);
			});
		}
		this.pict.AppData.EntityList.EditingID = pID;
		this.pict.AppData.EntityList.PendingMedia = [];
		this.pict.AppData.EntityList.FormTitle = 'Edit ' + libLabels.singular(this.pict, tmpDescriptor.Key, null, tmpDescriptor.Key);
		let tmpValues = {};
		tmpDescriptor.Fields.forEach((pField) =>
		{
			tmpValues[pField.F] = (tmpRow[pField.F] === null || typeof tmpRow[pField.F] === 'undefined')
				? (pField.E ? 0 : '')
				: tmpRow[pField.F];
		});
		this.pict.AppData.EntityList.FormValues = tmpValues;
		this.pict.AppData.EntityList.HiddenValues = {};
		this._enterForm();
	}

	setField(pField, pValue)
	{
		let tmpField = (this._descriptor() || { Fields: [] }).Fields.find((pF) => pF.F === pField);
		// AN ENTITY-BACKED SELECT HOLDS A NUMBER, NOT A STRING. Leaving one unpicked used to put '' in
		// FormValues and post it, and Postgres rejects '' for an integer column outright -- so creating
		// a module without choosing what it is built in came back a 500 with no record saved. SQLite
		// took the empty string without complaint, which is why this only shows up on a real database.
		this.pict.AppData.EntityList.FormValues[pField] = (tmpField && tmpField.E) ? (Number(pValue) || 0) : pValue;
		// If this field controls another field's visibility (HideWhen), rebuild the form so the dependent
		// fields show or hide. Only selects drive this (fired on change), so there is no text focus to lose.
		let tmpDescriptor = this._descriptor() || { Fields: [] };
		if (tmpDescriptor.Fields.some((pF) => pF.HideWhen && pF.HideWhen.Field === pField)) { this._enterForm(); }
		// A combo field that others depend on (Action depends on Capability) refreshes their suggestions
		// in place when it changes -- no re-render, so the text focus the person is typing in is not lost.
		if (tmpDescriptor.Fields.some((pF) => pF.T === 'combo' && pF.DependsOn === pField)) { this._populateComboDatalists(); }
	}

	// ── combo fields (type-or-pick <datalist>) ───────────────────────────────
	// Build the datalist suggestions for a combo field from a fetched source (today only
	// 'NodeCapabilities'). SourceKey picks which column becomes an option (Capability or Action);
	// DependsOn, when set, limits the actions to the capability already chosen.
	_comboOptions(pField, pValues)
	{
		let tmpRows = (this._comboSources && this._comboSources[pField.Source]) || null;
		if (!Array.isArray(tmpRows)) { return []; }
		let tmpKey = pField.SourceKey || 'Capability';
		let tmpDepend = pField.DependsOn ? String((pValues || {})[pField.DependsOn] || '') : '';
		let tmpSeen = {};
		let tmpOut = [];
		tmpRows.forEach((pRow) =>
		{
			if (tmpDepend && String((pRow && pRow.Capability) || '') !== tmpDepend) { return; }
			let tmpValue = String((pRow && pRow[tmpKey]) || '').trim();
			if (tmpValue && !tmpSeen[tmpValue]) { tmpSeen[tmpValue] = true; tmpOut.push({ Value: tmpValue }); }
		});
		return tmpOut;
	}

	// If the open form has any combo field sourced from NodeCapabilities, fetch that list once and fill
	// the datalists in place. The suggestions are a convenience: the inputs are usable text fields before
	// the fetch lands and if it fails, and free text always saves.
	_loadComboSources()
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) { return; }
		let tmpNeeds = (tmpDescriptor.Fields || []).some((pF) => pF.T === 'combo' && pF.Source === 'NodeCapabilities');
		if (!tmpNeeds) { return; }
		this._comboSources = this._comboSources || {};
		if (Array.isArray(this._comboSources.NodeCapabilities)) { this._populateComboDatalists(); return; }
		fetch('/1.0/NodeCapabilities', { method: 'GET', credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
			.then((pResponse) => pResponse.ok ? pResponse.json() : null)
			.then((pJSON) =>
			{
				this._comboSources.NodeCapabilities = (pJSON && Array.isArray(pJSON.Capabilities)) ? pJSON.Capabilities : [];
				this._populateComboDatalists();
			})
			.catch(() => { /* leave the combos as plain text inputs */ });
	}

	// Refill each combo field's <datalist> from the cached source, directly in the DOM, so no render
	// (and no editor remount) is needed. A cheap no-op outside the form or when nothing is cached yet.
	_populateComboDatalists()
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor || this.pict.AppData.EntityList.Mode !== 'form') { return; }
		let tmpValues = this.pict.AppData.EntityList.FormValues || {};
		(tmpDescriptor.Fields || []).forEach((pField) =>
		{
			if (pField.T !== 'combo') { return; }
			let tmpList = document.getElementById('EntityForm-combo-' + pField.F);
			if (!tmpList) { return; }
			tmpList.innerHTML = this._comboOptions(pField, tmpValues)
				.map((pOption) => '<option value="' + this._attr(pOption.Value) + '"></option>').join('');
		});
	}

	// ── code field (pict-section-code) ───────────────────────────────────────
	// A field declared T:'code' is a configuration blob, not prose. The editor gives it line numbers and
	// JSON highlighting so a person maintaining it from the web UI can see an unbalanced brace rather
	// than discovering it later as a node that silently stopped resolving a workspace (WI #456).

	// Called by the editor on every change: pull its content straight into FormValues so the existing
	// save() posts it with no special handling. Exactly what onMarkdownChanged does for prose.
	onCodeChanged()
	{
		if (!this._codeField) { return; }
		let tmpEditor = this.pict.views['Plansheet-FieldCodeEditor'];
		if (!tmpEditor || typeof tmpEditor.getCode !== 'function') { return; }
		this.pict.AppData.EntityList.FormValues[this._codeField] = tmpEditor.getCode();
	}

	// Destroy the editor when the form is left. The list render wipes its host DOM but the view persists,
	// so without this its CodeJar instance stays live against detached nodes. Idempotent.
	_teardownCodeEditor()
	{
		let tmpEditor = this.pict.views['Plansheet-FieldCodeEditor'];
		if (!tmpEditor || !tmpEditor.initialRenderComplete) { return; }
		try { tmpEditor.destroy(); tmpEditor.initialRenderComplete = false; }
		catch (pErr) { /* ignore -- teardown is best-effort */ }
	}

	// Mount (or remount) the editor into its host after the form renders, seeded from the field's current
	// FormValues. Mirrors _mountMarkdownEditor, including its fallback: an editor missing from the build
	// must leave a working textarea behind rather than an empty div and an unsavable field.
	_mountCodeEditor()
	{
		if (this.pict.AppData.EntityList.Mode !== 'form' || !this._codeField) { return; }
		let tmpEditor = this.pict.views['Plansheet-FieldCodeEditor'];
		let tmpValue = this.pict.AppData.EntityList.FormValues[this._codeField];
		tmpValue = (tmpValue === null || typeof tmpValue === 'undefined') ? '' : String(tmpValue);
		if (!tmpEditor)
		{
			this.pict.ContentAssignment.assignContent('#EntityForm-CodeHost',
				'<textarea class="ps-input" rows="10" id="EntityForm-CodeFallback"></textarea>');
			let tmpElement = (typeof document !== 'undefined') ? document.getElementById('EntityForm-CodeFallback') : null;
			if (tmpElement) { tmpElement.value = tmpValue; tmpElement.oninput = () => this.setField(this._codeField, tmpElement.value); }
			return;
		}
		this.pict.AppData.FieldCodeEditor = this.pict.AppData.FieldCodeEditor || {};
		this.pict.AppData.FieldCodeEditor.Code = tmpValue;
		try { if (tmpEditor.initialRenderComplete) { tmpEditor.destroy(); tmpEditor.initialRenderComplete = false; } } catch (pErr) { /* ignore */ }
		tmpEditor.render();
		if (typeof tmpEditor.marshalToView === 'function') { tmpEditor.marshalToView(); }
	}

	// ── rich markdown field (pict-section-markdowneditor) ────────────────────
	// The record being edited owns pasted media (OwnerType = entity key,
	// IDOwner = the editing id). The create form has no id yet (IDOwner 0), so the
	// editor uploads owned by 0 and registers each one via trackPendingMedia; save()
	// then re-owns them to the new record's id.
	markdownOwnerContext()
	{
		let tmpDescriptor = this._descriptor() || {};
		return { OwnerType: tmpDescriptor.Key || '', IDOwner: this.pict.AppData.EntityList.EditingID || 0 };
	}

	// The editor calls this for every image dropped on the create form (uploaded
	// owned by 0). We remember the id so save() can re-own it, or abandon can drop it.
	trackPendingMedia(pIDMedia)
	{
		if (!pIDMedia) { return; }
		let tmpState = this.pict.AppData.EntityList;
		if (!Array.isArray(tmpState.PendingMedia)) { tmpState.PendingMedia = []; }
		if (tmpState.PendingMedia.indexOf(pIDMedia) < 0) { tmpState.PendingMedia.push(pIDMedia); }
	}

	// After a create, re-own the create-form images to the new record so they show
	// in its gallery. The markdown already links them by /Media/:id/Blob, and the id
	// is stable across the re-own, so the saved Body needs no rewrite. Best-effort:
	// a failed re-own leaves the image linked and displayed, just not in the gallery.
	_reownPendingMedia(pOwnerType, pIDOwner)
	{
		let tmpState = this.pict.AppData.EntityList;
		let tmpPending = Array.isArray(tmpState.PendingMedia) ? tmpState.PendingMedia.slice() : [];
		tmpState.PendingMedia = [];
		let tmpMediaAPI = this.pict.providers.MediaAPI;
		if (!tmpMediaAPI || !pIDOwner || !tmpPending.length) { return; }
		tmpPending.forEach((pIDMedia) =>
		{
			tmpMediaAPI.reassignOwner(pIDMedia, pOwnerType, pIDOwner)
				.catch((pErr) => { if (this.pict && this.pict.log) { this.pict.log.warn('EntityList: could not re-own media ' + pIDMedia + ': ' + (pErr.message || pErr)); } });
		});
	}

	// Leaving the create form without saving: the uploads are orphans (owned by 0,
	// invisible in every gallery), so delete them best-effort. save() clears the
	// pending list before it exits, so a successful save never reaches this.
	_discardPendingMedia()
	{
		let tmpState = this.pict.AppData.EntityList;
		let tmpPending = Array.isArray(tmpState.PendingMedia) ? tmpState.PendingMedia.slice() : [];
		tmpState.PendingMedia = [];
		let tmpMediaAPI = this.pict.providers.MediaAPI;
		if (!tmpMediaAPI || !tmpPending.length) { return; }
		tmpPending.forEach((pIDMedia) =>
		{
			tmpMediaAPI.remove(pIDMedia).catch(() => { /* best-effort orphan cleanup */ });
		});
	}

	// Called by the editor on every change: pull its content straight into
	// FormValues so the existing save() posts it with no special handling.
	onMarkdownChanged()
	{
		if (!this._markdownField) { return; }
		let tmpEditor = this.pict.views['Plansheet-FieldMarkdownEditor'];
		if (!tmpEditor || typeof tmpEditor.getAllContent !== 'function') { return; }
		this.pict.AppData.EntityList.FormValues[this._markdownField] = tmpEditor.getAllContent('\n\n');
	}

	// Destroy the rich editor when we leave the form. _enterList()'s render wipes the editor's host
	// DOM, but the editor view persists (initialRenderComplete stays true, its CodeMirror instances +
	// timers stay live against now-detached nodes). Tear it down so it does not linger; this mirrors
	// the defensive destroy _mountMarkdownEditor does before a re-mount. Idempotent and safe to call
	// on every list entry (a no-op when no editor was rendered).
	_teardownMarkdownEditor()
	{
		let tmpEditor = this.pict.views['Plansheet-FieldMarkdownEditor'];
		if (!tmpEditor || !tmpEditor.initialRenderComplete) { return; }
		try { tmpEditor.destroy(); tmpEditor.initialRenderComplete = false; }
		catch (pErr) { /* ignore -- teardown is best-effort */ }
	}

	// Mount (or remount) the rich editor into its host after the form renders,
	// seeded from the field's current FormValues. Mirrors PictView-DocEditor.
	_mountMarkdownEditor()
	{
		if (this.pict.AppData.EntityList.Mode !== 'form' || !this._markdownField) { return; }
		let tmpEditor = this.pict.views['Plansheet-FieldMarkdownEditor'];
		let tmpVal = this.pict.AppData.EntityList.FormValues[this._markdownField];
		tmpVal = (tmpVal === null || typeof tmpVal === 'undefined') ? '' : String(tmpVal);
		if (!tmpEditor)
		{
			// The editor is not in this build: fall back to a plain textarea so the field still works.
			this.pict.ContentAssignment.assignContent('#EntityForm-MarkdownHost',
				'<textarea class="ps-input" rows="6" id="EntityForm-MarkdownFallback"></textarea>');
			let tmpEl = (typeof document !== 'undefined') ? document.getElementById('EntityForm-MarkdownFallback') : null;
			if (tmpEl) { tmpEl.value = tmpVal; tmpEl.oninput = () => this.setField(this._markdownField, tmpEl.value); }
			return;
		}
		this.pict.AppData.FieldMarkdownEditor = this.pict.AppData.FieldMarkdownEditor || {};
		this.pict.AppData.FieldMarkdownEditor.Segments = [{ Content: tmpVal }];
		try { if (tmpEditor.initialRenderComplete) { tmpEditor.destroy(); tmpEditor.initialRenderComplete = false; } } catch (pErr) { /* ignore */ }
		tmpEditor.render();
		tmpEditor.marshalToView();
		if (typeof tmpEditor.setPreviewMode === 'function') { tmpEditor.setPreviewMode('bottom'); }
	}

	setLikert(pField, pValue)
	{
		this.pict.AppData.EntityList.FormValues[pField] = Number(pValue) || 0;
		// Re-render the form so the selected segment lights up (a button click,
		// so there is no input focus to lose).
		this._enterForm();
	}

	/**
	 * Leave the form the way a person expects: back to the list, and with the URL agreeing.
	 *
	 * Deliberately the LIST and not the record just edited. That is exactly what closing the form has
	 * always put on screen, so this fixes the address bar without moving anything under anyone. Landing
	 * on the record instead would be defensible, but it is a change to where the product takes you and
	 * belongs in its own decision, not smuggled in with a routing fix.
	 *
	 * _enterList stays the fallback for an entity with no list route of its own.
	 */
	_leaveForm()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showEntityAfterForm === 'function'
			&& tmpApp.showEntityAfterForm(this.pict.AppData.EntityList.Key, 0)) { return; }
		this._enterList();
	}

	cancel() { this._leaveForm(); }

	open(pID)
	{
		let tmpDescriptor = this._descriptor();
		let tmpApp = this.pict.PictApplication;
		// A descriptor can route "Open" somewhere other than the generic CRUD
		// detail (Session hands the row to the transformer viewer via
		// openServerSession).
		if (tmpDescriptor && tmpDescriptor.OpenAction && tmpApp && typeof tmpApp[tmpDescriptor.OpenAction] === 'function')
		{
			return tmpApp[tmpDescriptor.OpenAction](pID);
		}
		if (tmpApp && typeof tmpApp.showEntityDetail === 'function')
		{
			tmpApp.showEntityDetail(this.pict.AppData.EntityList.Key, pID);
		}
	}

	save()
	{
		let tmpDescriptor = this._descriptor();
		let tmpProvider = this._provider();
		if (!tmpDescriptor || !tmpProvider) return;
		// Flush the rich markdown editor's current content into FormValues before posting.
		this.onMarkdownChanged();
		let tmpValues = this.pict.AppData.EntityList.FormValues || {};

		let tmpTitleField = tmpDescriptor.TitleField;
		if (!tmpValues[tmpTitleField] || !String(tmpValues[tmpTitleField]).trim())
		{
			return this._toast(tmpTitleField + ' is required.', 'error');
		}

		// Hidden prefill values (FKs from a create-from-here handoff) ride along;
		// form fields win on any key both carry.
		let tmpRecord = Object.assign({}, this.pict.AppData.EntityList.HiddenValues || {});
		tmpDescriptor.Fields.forEach((pField) =>
		{
			let tmpValue = tmpValues[pField.F];
			let tmpEmpty = (tmpValue === null || typeof tmpValue === 'undefined' || tmpValue === '');
			// A date input nobody filled in reads back as '', and '' is not a timestamp: Postgres rejects it
			// (22007 invalid input syntax) where SQLite quietly stores it. So a blank date saves against a local
			// SQLite dev database and fails against every deployed Postgres one. A date nobody set is null; ''
			// stays correct for text, textarea, and select.
			if (pField.T === 'date') { tmpRecord[pField.F] = tmpEmpty ? null : tmpValue; }
			else { tmpRecord[pField.F] = tmpEmpty ? '' : tmpValue; }
		});

		let tmpEditingID = this.pict.AppData.EntityList.EditingID;
		let tmpPromise = tmpEditingID
			? tmpProvider.update(tmpEditingID, tmpRecord)
			: tmpProvider.create(tmpRecord);

		tmpPromise
			.then((pSaved) =>
			{
				// A create returns the new record; re-own any create-form images to it so
				// they surface in the entity's gallery (edit-form images are already owned).
				if (!tmpEditingID && pSaved && pSaved[tmpDescriptor.IDField])
				{
					this._reownPendingMedia(tmpDescriptor.Key, pSaved[tmpDescriptor.IDField]);
				}
				// The record is saved. Announce it now, before the list refresh, so a refresh
				// hiccup can never turn a successful save into a "Save failed" message.
				this._toast(tmpDescriptor.Key + ' saved.', 'success');
				// Refresh the list, but treat a refresh failure as non-fatal -- the record is already
				// persisted -- so we still return to the list (rendered from cache) rather than
				// stranding the user on a blank screen after a successful create.
				return this._fetchPage(this.pict.AppData.EntityList.Page).catch((pErr) =>
				{
					if (this.pict && this.pict.log) { this.pict.log.warn('EntityList: post-save list refresh failed (' + ((pErr && pErr.message) || pErr) + '); returning to list from cache.'); }
				});
			})
			.then(() => this._leaveForm())
			.catch((pErr) => this._toast('Save failed: ' + (pErr.message || pErr), 'error'));
	}

	removeRecord(pID)
	{
		let tmpDescriptor = this._descriptor();
		if (!tmpDescriptor) return;
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpDo = () => this._doRemove(pID);
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Delete this ' + tmpDescriptor.Key + '?', { dangerous: true })
				.then((pOk) => { if (pOk) tmpDo(); });
		}
		else
		{
			tmpDo();
		}
	}

	_doRemove(pID)
	{
		let tmpProvider = this._provider();
		if (!tmpProvider) return;
		tmpProvider.remove(pID)
			.then(() => this._fetchPage(this.pict.AppData.EntityList.Page))
			.then(() => { this._toast('Deleted.', 'success'); this._enterList(); })
			.catch((pErr) => this._toast('Delete failed: ' + (pErr.message || pErr), 'error'));
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 3000 });
		}
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._mountMarkdownEditor();
		this._mountCodeEditor();
		this._populateComboDatalists();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewEntityList;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-EntityList',
	DefaultRenderable: 'EntityList-Display',
	AutoRender: false,
	CSS: /*css*/`
		/* root container styling comes from .ps-page (Shell) */
		.ps-list-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
		.ps-list-foot { display: flex; align-items: center; gap: 12px; justify-content: center; margin: 14px 0 4px; }
		.ps-list-foot-count { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); }
		/* Real pages, not a Load more (WI #433). The page label is centered between the two pairs of
		   controls, and the disabled ends stay in place rather than collapsing, so the buttons do not
		   move under the cursor as you walk a list. */
		.ps-pager { display: flex; align-items: center; gap: 6px; }
		.ps-pager-page { font-size: 13px; padding: 0 6px; white-space: nowrap; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-pager .ps-btn[disabled] { opacity: 0.45; cursor: default; }
		.ps-empty-actions { margin-top: 12px; }
		.ps-list-head h2 { font-size: 22px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); margin: 0; }
		.ps-list-head-actions { display: flex; align-items: center; gap: 8px; }
		/* The "Columns" show/hide picker: a button that opens a floating checkbox panel. */
		.ps-colmenu { position: relative; }
		.ps-colmenu > summary { list-style: none; cursor: pointer; }
		.ps-colmenu > summary::-webkit-details-marker { display: none; }
		.ps-colmenu-panel {
			position: absolute; right: 0; top: calc(100% + 6px); z-index: 30;
			min-width: 190px; padding: 8px; display: flex; flex-direction: column; gap: 2px;
			background: var(--theme-color-background-panel, #fff);
			border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px;
			box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
		}
		.ps-colmenu-item {
			display: flex; align-items: center; gap: 8px; padding: 4px 6px; border-radius: 6px;
			font-size: 13px; color: var(--theme-color-text-primary, #2c3140); cursor: pointer; white-space: nowrap;
		}
		.ps-colmenu-item:hover { background: var(--theme-color-background-hover, #eaf2ff); }
		.ps-colmenu-item input { cursor: pointer; }
		.ps-colmenu-item input:disabled { cursor: default; }
		.ps-btn {
			padding: 7px 14px; border: 1px solid var(--theme-color-border-default, #d8dde6);
			background: var(--theme-color-background-panel, #fff); border-radius: 8px;
			color: var(--theme-color-text-primary, #2c3140); cursor: pointer; font-size: 14px;
		}
		.ps-btn:hover { background: var(--theme-color-background-hover, #eaf2ff); }
		.ps-btn-primary { background: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-text-on-brand, #fff); border-color: transparent; }
		.ps-btn-primary:hover { filter: brightness(1.05); background: var(--theme-color-brand-primary, #2988df); }
		.ps-btn-sm { padding: 4px 10px; font-size: 13px; }
		.ps-btn-danger { color: var(--theme-color-status-danger, #c0392b); }
		/* Focus-view filter bar */
		.ps-filterbar { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px; }
		.ps-filter-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
		.ps-filter-search {
			flex: 1 1 240px; min-width: 180px; padding: 7px 10px; font-size: 14px; font-family: inherit;
			border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px;
			background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #2c3140);
		}
		.ps-filter-search:focus { outline: 2px solid var(--theme-color-brand-primary, #2988df); outline-offset: -1px; }
		.ps-filter-select {
			padding: 6px 8px; font-size: 13px; border-radius: 8px; max-width: 220px;
			border: 1px solid var(--theme-color-border-default, #d8dde6);
			background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140);
		}
		.ps-filter-views { font-weight: 600; }
		.ps-filter-spacer { flex: 1 1 auto; min-width: 4px; }
		.ps-filter-count { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); white-space: nowrap; }
		.ps-chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
		.ps-chip-row:empty { display: none; }
		.ps-chip {
			padding: 3px 11px; font-size: 12px; border-radius: 999px; cursor: pointer;
			border: 1px solid var(--theme-color-border-default, #d8dde6);
			background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376);
		}
		.ps-chip:hover { background: var(--theme-color-background-hover, #eaf2ff); }
		.ps-chip-on, .ps-chip-on:hover { background: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-text-on-brand, #fff); border-color: transparent; }
		/* The list can carry many columns (WorkItem: #, Title, Type, Status, Priority, Assignee,
		   Target, Sessions, Updated, Actions). When they exceed the content width, scroll the
		   table inside its own container rather than letting the rightmost columns clip off. */
		.ps-table-scroll { overflow-x: auto; border-radius: 10px; background: var(--theme-color-background-panel, #fff); }
			/* No overflow:hidden here, deliberately. It clipped the rounded corners, but it also silently
		   killed position:sticky on the mark cell: a sticky cell stops sticking the moment an ancestor
		   between it and the scrollport has a non-visible overflow, and it fails by simply scrolling
		   away rather than reporting anything. The .ps-table-scroll wrapper carries the same radius
		   and its own overflow, so the corners still clip. */
		.ps-table { width: 100%; border-collapse: collapse; background: var(--theme-color-background-panel, #fff); border-radius: 10px; }
		.ps-table th, .ps-table td { text-align: left; padding: 10px 14px; border-bottom: 1px solid var(--theme-color-border-default, #e6e9ee); font-size: 14px; white-space: nowrap; }
			/* Dates, counts, and badges stay on one line (never break mid-token).
			   The data cells shrink to their content (width:1px) so the surplus
			   width lands in the title/name column, which absorbs it and wraps
			   there instead of forcing odd breaks elsewhere. */
			.ps-table td { width: 1px; }
			.ps-table td.ps-cell-wrap { white-space: normal; width: 100%; min-width: 20rem; }
		.ps-table th { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-table tr:last-child td { border-bottom: none; }
		/* The whole body row is a click target that opens the record (#236). A pointer + a hover
		   wash advertise it; the actions cell keeps the default cursor since its buttons act. */
		.ps-table tbody tr.ps-row-open { cursor: pointer; }
		.ps-table tbody tr.ps-row-open:hover { background: var(--theme-color-background-hover, #eaf2ff); }
		.ps-table tbody tr.ps-row-open .ps-row-actions { cursor: default; }
		/* Inline-block (not flex) so the cell sizes to its buttons under the
		   width:1px shrink rule above; a flex cell collapses there. */
		.ps-row-actions { white-space: nowrap; text-align: right; }
		.ps-row-actions .ps-btn { display: inline-block; vertical-align: middle; }
		.ps-row-actions .ps-btn + .ps-btn { margin-left: 6px; }
		/* Addressed feedback reads as done: dim its content cells (never the actions, so
		   Reopen stays crisp) and give the Address / Reopen quick action a distinct tint. */
		.ps-row-addressed td:not(.ps-row-actions) { opacity: 0.5; }
		.ps-btn-address { color: var(--theme-color-brand-primary, #2988df); border-color: var(--theme-color-brand-primary, #2988df); }
		.ps-btn-reopen { color: var(--theme-color-text-secondary, #5b6376); }
		.ps-filter-addressed { font-weight: 600; }
		.ps-empty { text-align: center; padding: 48px 24px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-form { background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 10px; padding: 20px; max-width: 620px; }
			.ps-form-wide { max-width: 940px; }
			.ps-md-host { min-height: 360px; display: flex; flex-direction: column; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; overflow: hidden; }
			.ps-md-host > * { flex: 1 1 auto; min-width: 0; }
		.ps-form h3 { margin: 0 0 16px; color: var(--theme-color-text-primary, #2c3140); }
		.ps-field { margin-bottom: 14px; display: flex; flex-direction: column; gap: 6px; }
		.ps-field label { font-size: 13px; font-weight: 600; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-input {
			padding: 8px 10px; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 8px; font-size: 14px; font-family: inherit;
			background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #2c3140);
		}
		.ps-input:focus { outline: 2px solid var(--theme-color-brand-primary, #2988df); outline-offset: -1px; }
		.ps-form-actions { display: flex; gap: 8px; margin-top: 18px; }
		.ps-cell-badge {
			display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 12px;
			background: var(--theme-color-background-secondary, #eef1f6);
			color: var(--theme-color-text-secondary, #5b6376);
			border: 1px solid var(--theme-color-border-default, #e6e9ee); white-space: nowrap;
		}
		/* Assignee cell: an avatar + name. A human gets a round, name-colored initials disc;
		   an AI agent gets the violet hexagon (Vision #36's "AI as a first-class assignee"). */
		.ps-assignee { display: inline-flex; align-items: center; gap: 7px; white-space: nowrap; }
		.ps-avatar {
			flex: 0 0 auto; width: 24px; height: 24px; border-radius: 50%;
			display: inline-flex; align-items: center; justify-content: center;
			font-size: 10px; font-weight: 700; letter-spacing: 0.02em; color: #fff;
			background: var(--theme-color-text-secondary, #5b6376);
		}
		.ps-avatar svg { width: 14px; height: 14px; }
		/* The AI-agent hexagon: a six-sided violet badge, no border-radius (the clip makes the shape). */
		.ps-avatar-agent {
			border-radius: 0; color: #fff;
			background: var(--theme-color-brand-secondary, #7a5cd0);
			clip-path: polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%);
		}
		.ps-assignee-name { font-size: 13px; color: var(--theme-color-text-primary, #2c3140); }
		.ps-assignee-agent { color: var(--theme-color-brand-secondary, #7a5cd0); font-weight: 600; }
		/* Unassigned reads as a muted hollow disc + label, so the gap is legible not blank. */
		.ps-assignee-none .ps-avatar { background: transparent; border: 1px dashed var(--theme-color-border-default, #c3cad6); }
		.ps-assignee-none .ps-assignee-name { color: var(--theme-color-text-secondary, #8a94a6); font-style: italic; }
		/* Target cell: a type chip + the target's name. */
		.ps-target { display: inline-flex; align-items: center; gap: 7px; white-space: nowrap; }
		.ps-target-type {
			font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
			padding: 1px 6px; border-radius: 4px; color: #fff; background: var(--theme-color-text-secondary, #5b6376);
		}
		.ps-target-vision { background: #5a7fce; }
		.ps-target-module { background: #4aa3a3; }
		.ps-target-feature { background: #c9922f; }
		.ps-target-label { font-size: 13px; color: var(--theme-color-text-primary, #2c3140); max-width: 16rem; overflow: hidden; text-overflow: ellipsis; }
		/* Sessions cell: a robot glyph + count marking an auditable, session-backed work item. */
		.ps-sessions { display: inline-flex; align-items: center; gap: 4px; color: var(--theme-color-brand-secondary, #7a5cd0); }
		.ps-sessions svg { width: 15px; height: 15px; }
		.ps-sessions-count { font-size: 12px; font-weight: 600; }
		/* Type icon cell: a compact glyph standing in for a short enum (WorkItem Type, Feedback
		   Source). Compact density spends an icon's width instead of a word's, with the name in the
		   tooltip; expanded density draws the word beside the glyph (the gap only shows then). */
		.ps-type-icon { display: inline-flex; align-items: center; gap: 6px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-type-icon svg { width: 17px; height: 17px; }
		.ps-type-icon-label { font-size: 13px; white-space: nowrap; color: var(--theme-color-text-primary, #2c3140); }
		/* Compact/expanded density toggle: a small two-segment control by the Columns menu. */
		.ps-density { display: inline-flex; align-items: stretch; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; overflow: hidden; }
		.ps-density-btn { border: 0; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); padding: 4px 10px; font-size: 13px; cursor: pointer; }
		.ps-density-btn + .ps-density-btn { border-left: 1px solid var(--theme-color-border-default, #d8dde6); }
		.ps-density-btn:hover { background: var(--theme-color-background-hover, #eaf2ff); }
		.ps-density-on, .ps-density-on:hover { background: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-text-on-brand, #fff); }
		.ps-likert { display: flex; gap: 6px; }
		.ps-likert-btn { min-width: 38px; text-align: center; }
		.ps-likert-on, .ps-likert-on:hover { background: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-text-on-brand, #fff); border-color: transparent; }
		/* The mark cell: the row's designator (WI-014), and the column the eye comes back to. It
		   stays put while the table scrolls sideways, so a wide table never costs you the identity
		   of the row you are reading. Only this one column is sticky: the data cells are content
		   sized (width:1px above), so a second sticky column would need a left offset nobody can
		   compute. It needs its own background, since a sticky cell would otherwise be transparent
		   and the scrolled content would run underneath it. */
		.ps-table td.ps-cell-mark, .ps-table th.ps-th-mark {
			position: sticky; left: 0; z-index: 1;
			background: var(--theme-color-background-panel, #fff);
		}
		.ps-table tbody tr.ps-row-open:hover td.ps-cell-mark { background: var(--theme-color-background-hover, #eaf2ff); }
		/* Actions: two glyphs, not three labelled buttons. The row click opens the record, so the
		   column only carries what the row itself cannot do. Delete still routes through the confirm
		   modal, so the smaller target is strictly safer than the labelled button it replaces. */
		.ps-th-actions { width: 1px; }
		.ps-iconbtn {
			display: inline-flex; align-items: center; justify-content: center; vertical-align: middle;
			width: 26px; height: 26px; padding: 0; cursor: pointer; border-radius: 6px;
			border: 1px solid transparent; background: transparent;
			color: var(--theme-color-text-secondary, #5b6376);
		}
		.ps-iconbtn svg { width: 15px; height: 15px; }
		.ps-iconbtn:hover { background: var(--theme-color-background-hover, #eaf2ff); border-color: var(--theme-color-border-default, #d8dde6); }
		.ps-iconbtn-danger:hover { color: var(--theme-color-status-danger, #c0392b); }
		.ps-row-actions .ps-iconbtn + .ps-iconbtn { margin-left: 2px; }
		/* Concise mode: the scan. Same table, tighter rows, so roughly twice as many land on a
		   screen. The title column stops wrapping here, because a row that can grow to three lines
		   defeats the point of counting rows per screen. */
		.ps-table-concise th, .ps-table-concise td { padding: 3px 10px; font-size: 13px; }
		/* The title truncates here rather than growing the table. nowrap on its own made the cell as
		   wide as its longest title, which pushed the whole table past its container and gave the
		   concise scan the one thing it exists to avoid: a sideways scrollbar. max-width:0 with
		   width:100% is the table-layout idiom that makes a cell take the leftover width and ellipsis
		   the rest; the full title is on the row's tooltip and in table mode. */
		.ps-table-concise td.ps-cell-wrap { white-space: nowrap; min-width: 16rem; max-width: 0;
			overflow: hidden; text-overflow: ellipsis; }
		.ps-table-concise .ps-iconbtn { width: 22px; height: 22px; }
		.ps-table-concise .ps-avatar { width: 19px; height: 19px; font-size: 9px; }
		/* The concise column strip: which columns are on, and one click to pull another in. */
		.ps-pinbar { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 10px; }
		.ps-pinbar-label {
			font-size: 10px; letter-spacing: 0.13em; text-transform: uppercase;
			color: var(--theme-color-text-muted, #6c6a60); margin-right: 2px;
		}
		.ps-pin {
			padding: 2px 9px; font-size: 11.5px; font-family: inherit; cursor: pointer; border-radius: 999px;
			border: 1px solid var(--theme-color-border-default, #d8dde6);
			background: var(--theme-color-background-panel, #fff);
			color: var(--theme-color-text-secondary, #5b6376);
		}
		.ps-pin:hover { background: var(--theme-color-background-hover, #eaf2ff); }
		.ps-pin-on {
			border-color: var(--theme-color-brand-primary, #2988df);
			color: var(--theme-color-brand-primary, #2988df);
			font-weight: 600;
		}
	` + libMarkup.MarkupCSS,
	Templates:
	[
		...libMarkup.MarkupTemplates,
		{
			Hash: 'EntityList-Display',
			Template: /*html*/`
<div class="{~D:AppData.EntityList.PageClass~} ps-list">
	<div class="ps-list-head">
		<h2>{~D:AppData.EntityList.Title~}</h2>
		<div class="ps-list-head-actions">{~TS:EntityList-ViewMode:AppData.EntityList.ViewModeSlot~}{~TS:EntityList-ColumnsMenu:AppData.EntityList.ColumnsSlot~}{~TS:EntityList-NewBtn:AppData.EntityList.ListModeSlot~}{~TS:EntityList-UploadBtn:AppData.EntityList.CreateButtonSlot~}</div>
	</div>
	{~TS:EntityList-FilterBar:AppData.EntityList.FilterBarSlot~}
	{~TS:EntityList-PinBar:AppData.EntityList.PinSlot~}
	<div id="EntityList-Body">{~TS:EntityList-Table:AppData.EntityList.TableSlot~}{~TS:EntityList-Empty:AppData.EntityList.EmptySlot~}</div>
	<div id="EntityList-Foot">{~TS:EntityList-PageFoot:AppData.EntityList.PageFootSlot~}</div>
	{~TS:EntityList-Form:AppData.EntityList.FormSlot~}
</div>`
		},
		{ Hash: 'EntityList-NewBtn', Template: /*html*/`<button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityList'].newRecord()">+ New</button>` },
		{ Hash: 'EntityList-UploadBtn', Template: /*html*/`<button class="ps-btn ps-btn-primary" onclick="_Pict.PictApplication['{~D:Record.Action~}']()">{~D:Record.Label~}</button>` },
		{ Hash: 'EntityList-ColumnsMenu', Template: /*html*/`<details class="ps-colmenu"><summary class="ps-btn ps-btn-sm">Columns</summary><div class="ps-colmenu-panel">{~TS:EntityList-ColOption:Record.Columns~}</div></details>` },
		// The mode toggle: the concise scan against the full table. Titles spell out what each one
		// does for a first-time user, since the difference is a column count, not a font size.
		{ Hash: 'EntityList-ViewMode', Template: /*html*/`<div class="ps-density" role="group" aria-label="List mode"><button type="button" class="ps-density-btn{~D:Record.ConciseOnClass~}" title="Concise: the mark, the name, and the key columns" onclick="_Pict.views['Plansheet-EntityList'].setViewMode('concise')">Concise</button><button type="button" class="ps-density-btn{~D:Record.TableOnClass~}" title="Table: every column" onclick="_Pict.views['Plansheet-EntityList'].setViewMode('table')">Table</button></div>` },
		{ Hash: 'EntityList-ColOption', Template: /*html*/`<label class="ps-colmenu-item"><input type="checkbox" {~D:Record.CheckedAttr~} {~D:Record.DisabledAttr~} onchange="_Pict.views['Plansheet-EntityList'].toggleColumn('{~D:Record.F~}')"><span>{~D:Record.L~}</span></label>` },
		// The concise column strip: the same per-mode visibility the dropdown edits, laid out so
		// pulling a column in to cross-reference something is one click and the options are visible.
		{
			Hash: 'EntityList-PinBar',
			Template: /*html*/`<div class="ps-pinbar"><span class="ps-pinbar-label">Columns</span>{~TS:EntityList-Pin:Record.Pins~}</div>`
		},
		{ Hash: 'EntityList-Pin', Template: /*html*/`<button type="button" class="ps-pin{~D:Record.OnClass~}" onclick="_Pict.views['Plansheet-EntityList'].toggleColumn('{~D:Record.F~}')">{~D:Record.PinLabel~}</button>` },
		{
			Hash: 'EntityList-FilterBar',
			Template: /*html*/`
<div class="ps-filterbar">
	<div class="ps-filter-row">
		{~TS:EntityList-Filter-Search:Record.SearchSlot~}
		{~TS:EntityList-Filter-Addressed:Record.AddressedSlot~}
		{~TS:EntityList-Filter-Customer:Record.CustomerSlot~}
		{~TS:EntityList-Filter-SourceType:Record.SourceTypeSlot~}
		{~TS:EntityList-Filter-Views:Record.ViewSlot~}
		{~TS:EntityList-Filter-Type:Record.TypeSlot~}
		{~TS:EntityList-Filter-Priority:Record.PrioritySlot~}
		{~TS:EntityList-Filter-Assignee:Record.AssigneeSlot~}
		{~TS:EntityList-Filter-Target:Record.TargetSlot~}
		{~TS:EntityList-Filter-WorkflowStatus:Record.WorkflowStatusSlot~}
		{~TS:EntityList-Filter-Recent:Record.RecentSlot~}
		{~TS:EntityList-Filter-Save:Record.ViewSlot~}
		{~TS:EntityList-Filter-DeleteView:Record.DeleteViewSlot~}
		{~TS:EntityList-Filter-SetDefault:Record.SetDefaultSlot~}
		{~TS:EntityList-Filter-ClearDefault:Record.ClearDefaultSlot~}
		{~TS:EntityList-Filter-Clear:Record.ClearSlot~}
		<span class="ps-filter-spacer"></span>
		<span class="ps-filter-count" id="EntityList-Count">{~D:Record.CountLabel~}</span>
	</div>
	<div class="ps-chip-row">{~TS:EntityList-Chip:Record.StatusChips~}</div>
</div>`
		},
		{ Hash: 'EntityList-Filter-Search', Template: /*html*/`<input type="search" class="ps-filter-search" placeholder="Search..." value="{~D:Record.Value~}" oninput="_Pict.views['Plansheet-EntityList'].setSearch(this.value)">` },
		{ Hash: 'EntityList-Filter-Views', Template: /*html*/`<select class="ps-filter-select ps-filter-views" onchange="_Pict.views['Plansheet-EntityList'].applyView(this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-Type', Template: /*html*/`<select class="ps-filter-select" onchange="_Pict.views['Plansheet-EntityList'].setFilter('type', this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-Priority', Template: /*html*/`<select class="ps-filter-select" onchange="_Pict.views['Plansheet-EntityList'].setFilter('priority', this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-Assignee', Template: /*html*/`<select class="ps-filter-select" onchange="_Pict.views['Plansheet-EntityList'].setFilter('assignee', this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-Target', Template: /*html*/`<select class="ps-filter-select" onchange="_Pict.views['Plansheet-EntityList'].setFilter('target', this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-WorkflowStatus', Template: /*html*/`<select class="ps-filter-select" onchange="_Pict.views['Plansheet-EntityList'].setFilter('wfstatus', this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-Recent', Template: /*html*/`<select class="ps-filter-select" onchange="_Pict.views['Plansheet-EntityList'].setFilter('recent', this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-Addressed', Template: /*html*/`<select class="ps-filter-select ps-filter-addressed" onchange="_Pict.views['Plansheet-EntityList'].setFilter('addressed', this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-Customer', Template: /*html*/`<select class="ps-filter-select" onchange="_Pict.views['Plansheet-EntityList'].setFilter('customer', this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-SourceType', Template: /*html*/`<select class="ps-filter-select" onchange="_Pict.views['Plansheet-EntityList'].setFilter('sourcetype', this.value)">{~TS:EntityList-Filter-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Filter-Option', Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>` },
		{ Hash: 'EntityList-Filter-Save', Template: /*html*/`<button class="ps-btn ps-btn-sm" onclick="_Pict.views['Plansheet-EntityList'].saveView()">Save view</button>` },
		{ Hash: 'EntityList-Filter-SetDefault', Template: /*html*/`<button class="ps-btn ps-btn-sm" title="Make the current filters your default for this screen" onclick="_Pict.views['Plansheet-EntityList'].setAsDefault()">Set as default</button>` },
		{ Hash: 'EntityList-Filter-ClearDefault', Template: /*html*/`<button class="ps-btn ps-btn-sm" title="Remove your saved default; revert to the standard default" onclick="_Pict.views['Plansheet-EntityList'].clearDefault()">Clear default</button>` },
		{ Hash: 'EntityList-Filter-DeleteView', Template: /*html*/`<button class="ps-btn ps-btn-sm ps-btn-danger" onclick="_Pict.views['Plansheet-EntityList'].deleteView('{~D:Record.Key~}')">Delete view</button>` },
		{ Hash: 'EntityList-Filter-Clear', Template: /*html*/`<button class="ps-btn ps-btn-sm" onclick="_Pict.views['Plansheet-EntityList'].clearFilters()">Clear</button>` },
		{ Hash: 'EntityList-Chip', Template: /*html*/`<button class="ps-chip{~D:Record.OnClass~}" onclick="_Pict.views['Plansheet-EntityList'].toggleStatus('{~D:Record.Key~}')">{~D:Record.Label~}</button>` },
		{
			Hash: 'EntityList-Table',
			Template: /*html*/`
<div class="ps-table-scroll">
<table class="ps-table{~D:AppData.EntityList.TableClass~}">
	<thead><tr id="EntityList-HeadRow">{~TS:EntityList-Th:AppData.EntityList.HeaderCells~}<th class="ps-th-actions"></th></tr></thead>
	<tbody id="EntityList-Tbody">{~TS:EntityList-Row:AppData.EntityList.Rows~}</tbody>
</table>
</div>`
		},
		{
			Hash: 'EntityList-PageFoot',
			Template: /*html*/`<div class="ps-list-foot"><span class="ps-list-foot-count">{~D:Record.RangeLabel~}</span><nav class="ps-pager" aria-label="Pages"><button class="ps-btn ps-btn-sm" {~D:Record.FirstDisabled~} title="First page" onclick="_Pict.views['Plansheet-EntityList'].goToPage(1)">&laquo; First</button><button class="ps-btn ps-btn-sm" {~D:Record.PrevDisabled~} title="Previous page" onclick="_Pict.views['Plansheet-EntityList'].goToPage({~D:Record.PrevPage~})">&lsaquo; Prev</button><span class="ps-pager-page">{~D:Record.PageLabel~}</span><button class="ps-btn ps-btn-sm" {~D:Record.NextDisabled~} title="Next page" onclick="_Pict.views['Plansheet-EntityList'].goToPage({~D:Record.NextPage~})">Next &rsaquo;</button><button class="ps-btn ps-btn-sm" {~D:Record.LastDisabled~} title="Last page" onclick="_Pict.views['Plansheet-EntityList'].goToPage({~D:Record.LastPage~})">Last &raquo;</button></nav></div>`
		},
		{ Hash: 'EntityList-Th', Template: /*html*/`<th class="ps-th-sort{~D:Record.ThClass~}" style="cursor:pointer;user-select:none;white-space:nowrap" onclick="_Pict.views['Plansheet-EntityList'].sortBy({~D:Record.Index~})">{~D:Record.Label~}{~D:Record.Caret~}</th>` },
		{
			Hash: 'EntityList-Row',
			// The whole row opens the record (#236), in both modes, so an explicit "Open" button was
			// only ever repeating the row click. Dropping it and reducing Edit / Delete to glyphs
			// takes the actions cell from 257px to roughly a quarter of that, on every row of every
			// list; that column was 22% of the Work Items table and 28% of the Features table, and it
			// was what scrolled off the right edge when the table outgrew its container.
			//
			// The Address / Reopen quick action stays a labelled button: clearing a feedback backlog
			// from the list without opening anything is the whole point of it (#201).
			//
			// The actions cell stops the click from bubbling so its buttons still do their own thing.
			Template: /*html*/`<tr class="ps-row-open {~D:Record.RowClass~}" onclick="_Pict.views['Plansheet-EntityList'].open({~D:Record.ID~})">{~TS:EntityList-Cell:Record.Cells~}<td class="ps-row-actions" onclick="event.stopPropagation()">{~TS:EntityList-AddressBtn:Record.AddressSlot~}<button class="ps-iconbtn" title="Edit" aria-label="Edit" onclick="_Pict.views['Plansheet-EntityList'].editRecord({~D:Record.ID~})">{~I:Edit~}</button><button class="ps-iconbtn ps-iconbtn-danger" title="Delete" aria-label="Delete" onclick="_Pict.views['Plansheet-EntityList'].removeRecord({~D:Record.ID~})">{~I:Trash~}</button></td></tr>`
		},
		{ Hash: 'EntityList-AddressBtn', Template: /*html*/`<button class="ps-btn ps-btn-sm{~D:Record.OnClass~}" onclick="_Pict.views['Plansheet-EntityList'].toggleAddressed({~D:Record.ID~})">{~D:Record.Label~}</button>` },
		{ Hash: 'EntityList-Cell', Template: /*html*/`<td class="{~D:Record.CellClass~}" {~D:Record.TitleAttr~}>{~TS:EntityList-CellPlain:Record.PlainSlot~}{~TS:EntityList-CellBadge:Record.BadgeSlot~}{~TS:EntityList-CellAssignee:Record.AssigneeSlot~}{~TS:EntityList-CellTarget:Record.TargetSlot~}{~TS:EntityList-CellSession:Record.SessionSlot~}{~TS:EntityList-CellIcon:Record.IconSlot~}{~TS:Markup-Designator:Record.MarkSlot~}</td>` },
		{ Hash: 'EntityList-CellIcon', Template: /*html*/`<span class="ps-type-icon" title="{~D:Record.Title~}">{~D:Record.Html~}{~TS:EntityList-CellIconLabel:Record.LabelSlot~}</span>` },
		{ Hash: 'EntityList-CellIconLabel', Template: /*html*/`<span class="ps-type-icon-label">{~D:Record.Text~}</span>` },
		{ Hash: 'EntityList-CellPlain', Template: /*html*/`{~D:Record.Value~}` },
		{ Hash: 'EntityList-CellBadge', Template: /*html*/`<span class="ps-cell-badge" {~D:Record.StyleAttr~}>{~D:Record.Value~}</span>` },
		// Assignee: a round initials avatar (human) or a violet hexagon with a robot glyph (AI),
		// then the name. The agent hexagon is a clip-path shape; the human avatar is a circle.
		{ Hash: 'EntityList-CellAssignee', Template: /*html*/`<span class="ps-assignee{~D:Record.RowClass~}"><span class="ps-avatar{~D:Record.AvatarClass~}" {~D:Record.AvatarStyle~}>{~D:Record.Initials~}{~TS:EntityList-AgentGlyph:Record.AssigneeAgentSlot~}</span><span class="ps-assignee-name{~D:Record.NameClass~}">{~D:Record.Name~}</span></span>` },
		{ Hash: 'EntityList-CellTarget', Template: /*html*/`<span class="ps-target"><span class="ps-target-type{~D:Record.TypeClass~}">{~D:Record.Type~}</span><span class="ps-target-label">{~D:Record.Label~}</span></span>` },
		{ Hash: 'EntityList-CellSession', Template: /*html*/`<span class="ps-sessions" title="{~D:Record.Title~}">{~I:Robot~}<span class="ps-sessions-count">{~D:Record.Count~}</span></span>` },
		{ Hash: 'EntityList-AgentGlyph', Template: /*html*/`{~I:Robot~}` },
		{ Hash: 'EntityList-Empty', Template: /*html*/`<div class="ps-empty">{~D:AppData.EntityList.EmptyText~}{~TS:EntityList-EmptyClear:AppData.EntityList.EmptyFilteredSlot~}</div>` },
		{ Hash: 'EntityList-EmptyClear', Template: /*html*/`<div class="ps-empty-actions"><button class="ps-btn ps-btn-sm" onclick="_Pict.views['Plansheet-EntityList'].clearFilters()">Clear filters</button></div>` },
		{
			Hash: 'EntityList-Form',
			Template: /*html*/`
<div class="ps-form {~D:AppData.EntityList.FormClass~}">
	<h3>{~D:AppData.EntityList.FormTitle~}</h3>
	{~TS:EntityList-Field:AppData.EntityList.FormFields~}
	<div class="ps-form-actions">
		<button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityList'].save()">Save</button>
		<button class="ps-btn" onclick="_Pict.views['Plansheet-EntityList'].cancel()">Cancel</button>
	</div>
</div>`
		},
		{
			Hash: 'EntityList-Field',
			Template: /*html*/`
<div class="ps-field">
	<label>{~D:Record.L~}</label>
	{~TS:EntityList-Text:Record.TextSlot~}
	{~TS:EntityList-Textarea:Record.TextareaSlot~}
	{~TS:EntityList-Markdown:Record.MarkdownSlot~}
	{~TS:EntityList-Code:Record.CodeSlot~}
	{~TS:EntityList-Select:Record.SelectSlot~}
	{~TS:EntityList-Combo:Record.ComboSlot~}
	{~TS:EntityList-Date:Record.DateSlot~}
	{~TS:EntityList-Likert:Record.LikertSlot~}
	{~TS:EntityList-Checkbox:Record.CheckboxSlot~}
</div>`
		},
		{ Hash: 'EntityList-Text', Template: /*html*/`<input type="text" class="ps-input" value="{~D:Record.Value~}" oninput="_Pict.views['Plansheet-EntityList'].setField('{~D:Record.F~}', this.value)">` },
		{ Hash: 'EntityList-Textarea', Template: /*html*/`<textarea class="ps-input" rows="3" oninput="_Pict.views['Plansheet-EntityList'].setField('{~D:Record.F~}', this.value)">{~D:Record.Value~}</textarea>` },
		{ Hash: 'EntityList-Markdown', Template: /*html*/`<div class="ps-md-host" id="EntityForm-MarkdownHost"></div>` },
		{ Hash: 'EntityList-Code', Template: /*html*/`<div class="ps-code-host" id="EntityForm-CodeHost"></div>` },
		{ Hash: 'EntityList-Select', Template: /*html*/`<select class="ps-input" onchange="_Pict.views['Plansheet-EntityList'].setField('{~D:Record.F~}', this.value)">{~TS:EntityList-Option:Record.Options~}</select>` },
		{ Hash: 'EntityList-Date', Template: /*html*/`<input type="date" class="ps-input" value="{~D:Record.Value~}" oninput="_Pict.views['Plansheet-EntityList'].setField('{~D:Record.F~}', this.value)">` },
		{ Hash: 'EntityList-Checkbox', Template: /*html*/`<label class="ps-form-check"><input type="checkbox" class="ps-form-checkbox" {~D:Record.CheckedAttr~} onchange="_Pict.views['Plansheet-EntityList'].setField('{~D:Record.F~}', this.checked ? 1 : 0)"></label>` },
		{ Hash: 'EntityList-Combo', Template: /*html*/`<input type="text" class="ps-input" list="{~D:Record.ListID~}" value="{~D:Record.Value~}" oninput="_Pict.views['Plansheet-EntityList'].setField('{~D:Record.F~}', this.value)"><datalist id="{~D:Record.ListID~}">{~TS:EntityList-ComboOption:Record.Options~}</datalist>` },
		{ Hash: 'EntityList-ComboOption', Template: /*html*/`<option value="{~D:Record.Value~}"></option>` },
		{ Hash: 'EntityList-Option', Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Value~}</option>` },
		{ Hash: 'EntityList-Likert', Template: /*html*/`<div class="ps-likert">{~TS:EntityList-LikertBtn:Record.Buttons~}</div>` },
		{ Hash: 'EntityList-LikertBtn', Template: /*html*/`<button type="button" class="ps-btn ps-btn-sm ps-likert-btn{~D:Record.OnClass~}" onclick="_Pict.views['Plansheet-EntityList'].setLikert('{~D:Record.F~}', {~D:Record.N~})">{~D:Record.Label~}</button>` }
	],
	Renderables:
	[
		{
			RenderableHash: 'EntityList-Display',
			TemplateHash: 'EntityList-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
